// pages/SapBasisMonitor.js
// SAP Basis Health Monitor — tampilan TAB (KPI strip + tab per seksi).
// Konsumsi GET /api/sap/health (modul sapBasisMonitor.js).
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth, api as axiosApi } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import { usePageAIContext } from '../context/AIPageContext';
import {
  Activity, RefreshCw, Server, Cpu, AlertTriangle, Lock, Database, Bug, KeyRound, HardDrive, Eye, X, FileText,
  Users, UserX, BarChart3, FileSpreadsheet, Copy,
} from 'lucide-react';
import useIsMobile from '../hooks/useIsMobile';
import CardList from '../components/mobile/CardList';
import DetailSheet from '../components/mobile/DetailSheet';
import { useSearchParams } from 'react-router-dom';
import { CronScheduler, Alert } from './tmShared';
import useTabDeepLink from '../hooks/useTabDeepLink';
import AseMonconfigTrend from '../components/AseMonconfigTrend';
import AseSpaceTrend from '../components/AseSpaceTrend';
import AseJobsPanel from '../components/AseJobsPanel';
import AseTableStats from '../components/AseTableStats';
import ClientCopyPanel from '../components/ClientCopyPanel';
import ClientCopyPrepPanel from '../components/ClientCopyPrepPanel';
import { tabsFor } from '../searchRegistry';

// Tab yang boleh datang dari `?tab=` (QuickSearch). Daftar ini sengaja terpisah
// dari `kpis` di dalam komponen: `kpis` dibangun dari data yang baru tiba
// setelah fetch, sedangkan validasi deep link harus jalan pada render pertama.
// Source: searchRegistry.js (pageKey 'sap_basis_mon'). A tab in `kpis` that is
// not registered there is NOT rendered, so register it first.
const BASIS_TABS = tabsFor('sap_basis_mon');

// ── useApi: bungkus axios instance, kembalikan body JSON ──────────────────────
function useApi() {
  return useCallback(async (path, opts = {}) => {
    const method = (opts.method || 'GET').toLowerCase();
    const body   = opts.body ? JSON.parse(opts.body) : undefined;
    try {
      const res = (method === 'get' || method === 'delete')
        ? await axiosApi[method](path)
        : await axiosApi[method](path, body);
      return res.data;
    } catch (err) { throw new Error(err.response?.data?.error || err.message || 'API error'); }
  }, []);
}

// ── Util format ───────────────────────────────────────────────────────────────
const fmt = (n) => Number(n || 0).toLocaleString();
const fmtN = (n) => (n === null || n === undefined || n === '') ? '—' : Number(n).toLocaleString();
const fmtDate = (d) => { const s = String(d || '').trim(); return /^\d{8}$/.test(s) ? `${s.slice(0,4)}-${s.slice(4,6)}-${s.slice(6,8)}` : (s || '—'); };
const fmtTime = (t) => { const s = String(t || '').trim(); return /^\d{6}$/.test(s) ? `${s.slice(0,2)}:${s.slice(2,4)}:${s.slice(4,6)}` : (s || ''); };

// ── Histori KPI (audit trail) — parsing & konfigurasi kolom ─────────────────
// disk_summary disimpan backend sbg JSON string [{fs,use}] — ringkas jadi
// "maks 78% (/oracle)" atau '—' kalau kosong/tak valid.
function diskSummaryShort(s) {
  if (!s) return '—';
  try {
    const arr = JSON.parse(s);
    if (!Array.isArray(arr) || !arr.length) return 'OK';
    let best = null;
    arr.forEach(r => { const n = parseInt(String(r.use || '').replace('%', ''), 10); if (!isNaN(n) && (!best || n > best.n)) best = { n, fs: r.fs }; });
    return best ? `maks ${best.n}% (${best.fs || '?'})` : 'OK';
  } catch (_) { return '—'; }
}
// Field histori: keys dipakai utk cek perubahan, fmt dipakai utk tampilan sel,
// narrative dipakai utk baris ekspansi (before → after).
const HIST_FIELDS = [
  { keys: ['work_proc_busy', 'work_proc_total'], label: 'Work Proc', fmt: r => `${fmtN(r.work_proc_busy)}/${fmtN(r.work_proc_total)}` },
  { keys: ['locked_users'], label: 'Locked', fmt: r => fmtN(r.locked_users) },
  { keys: ['logged_on_users', 'logged_on_sessions'], label: 'Logged On', fmt: r => `${fmtN(r.logged_on_users)}/${fmtN(r.logged_on_sessions)}` },
  { keys: ['multi_logon'], label: 'Multi Logon', fmt: r => fmtN(r.multi_logon) },
  { keys: ['failed_jobs'], label: 'Failed Jobs', fmt: r => fmtN(r.failed_jobs) },
  { keys: ['dumps'], label: 'Dumps', fmt: r => fmtN(r.dumps) },
  { keys: ['lock_sm12'], label: 'Lock SM12', fmt: r => fmtN(r.lock_sm12) },
  { keys: ['update_errors'], label: 'Update Err', fmt: r => fmtN(r.update_errors) },
  { keys: ['disk_summary'], label: 'Disk', fmt: r => diskSummaryShort(r.disk_summary) },
  { keys: ['sybase_log_lines'], label: 'Sybase Log', fmt: r => fmtN(r.sybase_log_lines) },
];
// true bila salah satu key field ini berbeda antara baris `r` dan pembanding `prev`.
function histFieldChanged(field, r, prev) {
  if (!prev) return false;
  return field.keys.some(k => String(r[k] ?? '') !== String(prev[k] ?? ''));
}

// ── Diff isi/konten (bukan cuma angka) — bandingkan daftar/teks antar 2 payload ──
// keyFn dipakai utk identitas 1 item (mis. nama job + waktu mulai), supaya bisa
// tahu item mana yg BARU muncul vs yg HILANG (selesai/dibersihkan).
function diffByKey(curList, prevList, keyFn) {
  const cur = Array.isArray(curList) ? curList : [];
  const prv = Array.isArray(prevList) ? prevList : [];
  const prevKeys = new Set(prv.map(keyFn));
  const curKeys  = new Set(cur.map(keyFn));
  return {
    added:   cur.filter(x => !prevKeys.has(keyFn(x))),
    removed: prv.filter(x => !curKeys.has(keyFn(x))),
  };
}
// Diff baris teks (mis. tail Sybase ASE errorlog) — berbasis Set, cukup akurat
// utk log yg bersifat rolling/tail (bukan diff posisi per baris).
function diffLines(curText, prevText) {
  const cur = String(curText || '').split('\n').filter(Boolean);
  const prv = String(prevText || '').split('\n').filter(Boolean);
  const prevSet = new Set(prv);
  const curSet  = new Set(cur);
  return {
    added:   cur.filter(l => !prevSet.has(l)),
    removed: prv.filter(l => !curSet.has(l)),
  };
}
// Konfigurasi per label HIST_FIELDS yg isinya berupa DAFTAR (bisa di-diff per
// item). Work Proc & Update Err/Logged/dsb generik → sengaja dibatasi ke yg
// paling berguna utk audit (siapa/apa yg baru vs hilang).
const CONTENT_DIFF_CONFIG = {
  'Failed Jobs': {
    getList: (p) => p?.failedJobs?.ok ? (p.failedJobs.rows || []) : null,
    keyFn: r => `${r.JOBNAME}|${r.JOBCOUNT}|${r.STRTDATE}|${r.STRTTIME}`,
    render: r => `${r.JOBNAME || '?'} · ${fmtDate(r.STRTDATE)} ${fmtTime(r.STRTTIME)} · user: ${r.SDLUNAME || '-'}`,
  },
  'Dumps': {
    getList: (p) => p?.shortDumps?.ok ? (p.shortDumps.rows || []) : null,
    keyFn: r => `${r.DATUM}|${r.UZEIT}|${r.UNAME}|${r.MODNO}|${r.AHOST}`,
    render: r => `${fmtDate(r.DATUM)} ${fmtTime(r.UZEIT)} · ${r.RUNTIME_ERROR || '?'} (${r.PROGRAM || '-'}) · user: ${r.UNAME || '-'}`,
  },
  'Locked': {
    getList: (p) => p?.lockedUsers?.ok ? (p.lockedUsers.rows || []) : null,
    keyFn: r => r.BNAME,
    render: r => `${r.BNAME} — ${r.LOCK_REASON || '-'}`,
  },
  'Logged On': {
    getList: (p) => p?.loggedOnUsers?.ok ? (p.loggedOnUsers.rows || []) : null,
    keyFn: r => `${r.BNAME}|${r.TERMINAL}|${r.LOGON_DATE}|${r.LOGON_TIME}`,
    render: r => `${r.BNAME} @ ${r.TERMINAL || r.IP || '-'} · ${r.LOGON_DATE || ''} ${r.LOGON_TIME || ''}`,
  },
  'Multi Logon': {
    getList: (p) => p?.multiLogon?.ok ? (p.multiLogon.rows || []) : null,
    keyFn: r => r.BNAME,
    render: r => `${r.BNAME} — ${r.SESSIONS || '?'} sesi (${r.TERMINALS || '-'})`,
  },
  'Lock SM12': {
    getList: (p) => p?.lockEntries?.ok ? (p.lockEntries.rows || []) : null,
    keyFn: r => JSON.stringify(r),
    render: r => Object.entries(r).slice(0, 4).map(([k, v]) => `${k}:${v}`).join(' · ') || '(baris kosong)',
  },
  'Update Err': {
    getList: (p) => p?.updateErrors?.ok ? (p.updateErrors.rows || []) : null,
    keyFn: r => r.VBKEY || JSON.stringify(r),
    render: r => `${r.VBKEY || '?'} · ${r.VBTCODE || '-'} · by ${r.VBUSR || '-'} · rc=${r.VBRC || '-'}`,
  },
  'Disk': {
    getList: (p) => p?.disk?.ok ? (p.disk.rows || []) : null,
    keyFn: r => r.Filesystem || JSON.stringify(r),
    render: r => `${r.Filesystem || '?'} — ${r['Use%'] || '-'} used`,
  },
};

// ── Panel detail perubahan (per baris histori) ──────────────────────────────
// Tampil saat baris di-expand: narasi angka (sudah ada) + diff ISI per seksi
// yg berubah (item baru/hilang utk daftar, baris baru/hilang utk Sybase Log).
function HistDetailDiff({ t, changedFields, curPayload, prevPayload, loadingCur, loadingPrev }) {
  if (loadingCur || loadingPrev) {
    return <div style={{ marginTop: 8, color: '#94a3b8', fontSize: 12 }}>{t('basis.historyLoadingDetail')}</div>;
  }
  if (curPayload === null || (prevPayload === null)) {
    return <div style={{ marginTop: 8, color: '#dc2626', fontSize: 12 }}>{t('basis.historyDetailError')}</div>;
  }
  if (!curPayload || !prevPayload) return null; // belum sempat dimuat

  const sections = [];

  changedFields.forEach(f => {
    if (f.label === 'Sybase Log') {
      const curText  = curPayload?.sybaseLog?.ok  ? (curPayload.sybaseLog.text || '')  : '';
      const prevText = prevPayload?.sybaseLog?.ok ? (prevPayload.sybaseLog.text || '') : '';
      const d = diffLines(curText, prevText);
      if (d.added.length || d.removed.length) sections.push({ label: f.label, added: d.added, removed: d.removed, isText: true });
      return;
    }
    const cfg = CONTENT_DIFF_CONFIG[f.label];
    if (!cfg) return; // mis. Work Proc — tak ada diff isi, angka saja sudah cukup
    const curList  = cfg.getList(curPayload);
    const prevList = cfg.getList(prevPayload);
    if (curList === null || prevList === null) return; // seksi error/tak diambil di salah satu sisi
    const d = diffByKey(curList, prevList, cfg.keyFn);
    if (d.added.length || d.removed.length) {
      sections.push({ label: f.label, added: d.added.map(cfg.render), removed: d.removed.map(cfg.render) });
    }
  });

  if (!sections.length) return null;

  return (
    <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {sections.map(s => (
        <div key={s.label} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 10px' }}>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>{s.label}</div>
          {s.added.length > 0 && (
            <div style={{ marginBottom: s.removed.length ? 4 : 0 }}>
              {s.added.slice(0, 20).map((line, i) => (
                <div key={i} style={{ fontSize: 12, color: '#166534', fontFamily: s.isText ? 'monospace' : 'inherit' }}>+ {line}</div>
              ))}
              {s.added.length > 20 && <div style={{ fontSize: 11, color: '#94a3b8' }}>… {t('basis.historyMoreLines', { n: s.added.length - 20 })}</div>}
            </div>
          )}
          {s.removed.length > 0 && (
            <div>
              {s.removed.slice(0, 20).map((line, i) => (
                <div key={i} style={{ fontSize: 12, color: '#991b1b', fontFamily: s.isText ? 'monospace' : 'inherit', textDecoration: s.isText ? 'none' : 'line-through' }}>− {line}</div>
              ))}
              {s.removed.length > 20 && <div style={{ fontSize: 11, color: '#94a3b8' }}>… {t('basis.historyMoreLines', { n: s.removed.length - 20 })}</div>}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── UI primitives ─────────────────────────────────────────────────────────────
function Badge({ text, color = '#64748b' }) {
  return <span style={{ background: color + '18', color, border: `1px solid ${color}33`, borderRadius: 20, padding: '2px 9px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>{text}</span>;
}
function Th({ children, w }) { return <th style={{ background: '#f8fafc', padding: '8px 12px', textAlign: 'left', fontWeight: 600, fontSize: 11, color: '#64748b', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.04em', width: w }}>{children}</th>; }
function Td({ children, title, mono }) { return <td title={title} style={{ padding: '7px 12px', borderBottom: '1px solid #f1f5f9', fontSize: 13, color: '#1e293b', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: mono ? 'monospace' : 'inherit' }}>{children ?? '—'}</td>; }
function Table({ head, children }) {
  return (
    <div style={{ overflowX: 'auto', border: '1px solid #f1f5f9', borderRadius: 10 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr>{head}</tr></thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}
// Tabel dinamis: kolom dari union key baris (untuk seksi yang strukturnya belum pasti).
// Mobile: baris jadi kartu — tap membuka bottom-sheet berisi semua kolom.
function DynamicTable({ rows, preferred = [] }) {
  const isMobile = useIsMobile();
  const [openRow, setOpenRow] = useState(null); // index baris → DetailSheet
  const cols = useMemo(() => {
    const set = new Set();
    rows.forEach(r => Object.keys(r).forEach(k => set.add(k)));
    const present = [...set];
    const head = preferred.filter(p => set.has(p));
    return [...head, ...present.filter(p => !head.includes(p))];
  }, [rows, preferred]);
  if (!rows.length) return null;
  if (isMobile) {
    const items = rows.map((r, i) => ({ __i: i, ...r }));
    const r = openRow != null ? rows[openRow] : null;
    return (
      <>
        <CardList
          items={items}
          keyOf={(x) => String(x.__i)}
          title={(x) => String(x[cols[0]] ?? '—')}
          badge={null}
          subtitle={(x) => cols.slice(1, 4).map(c => x[c]).filter(v => v !== null && v !== undefined && v !== '').join(' · ') || '—'}
          onOpen={(x) => setOpenRow(x.__i)}
        />
        {r && (
          <DetailSheet
            open
            title={String(r[cols[0]] ?? '—')}
            fields={cols.map(c => ({ label: c, value: r[c] != null ? String(r[c]) : null }))}
            onClose={() => setOpenRow(null)}
          />
        )}
      </>
    );
  }
  return (
    <Table head={cols.map(c => <Th key={c}>{c}</Th>)}>
      {rows.map((r, i) => <tr key={i}>{cols.map(c => <Td key={c} mono title={r[c]}>{r[c]}</Td>)}</tr>)}
    </Table>
  );
}
function SectionError({ msg }) {
  return (
    <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', borderRadius: 8, padding: '10px 14px', fontSize: 12.5, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
      <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
      <span style={{ wordBreak: 'break-word' }}>{msg || 'Gagal memuat seksi ini.'}</span>
    </div>
  );
}
function Empty({ text }) { return <div style={{ padding: '1.75rem', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>{text}</div>; }
function Pending({ text }) {
  return (
    <div style={{ padding: '1.5rem', textAlign: 'center', color: '#94a3b8', fontSize: 13, background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 10 }}>
      <HardDrive size={26} style={{ opacity: 0.5, marginBottom: 8 }} /><div>{text}</div>
    </div>
  );
}

const WP_TYPE_COLOR = { DIA: '#2563eb', BTC: '#7c3aed', UPD: '#0891b2', UP2: '#0e7490', SPO: '#d97706', ENQ: '#dc2626', BGD: '#16a34a' };

// ── Page ────────────────────────────────────────────────────────────────────
export default function SapBasisMonitor() {
  const { user } = useAuth();
  const {
    lang,
    t
  } = useLang();
  const id = lang !== 'en';
  const api = useApi();

  const [profiles, setProfiles] = useState([]);
  const [connId, setConnId]     = useState('');
  const [lookback, setLookback] = useState(1);
  const [cronOpen, setCronOpen] = useState(false);
  const [histOpen, setHistOpen]       = useState(false);
  const [histLoading, setHistLoading] = useState(false);
  const [histErr, setHistErr]         = useState('');
  const [histRows, setHistRows]       = useState([]);
  const [histBaseline, setHistBaseline] = useState(null);
  const histTodayISO = () => new Date().toISOString().slice(0, 10);
  const [histFrom, setHistFrom] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10); });
  const [histTo, setHistTo]     = useState(histTodayISO());
  const [histExpanded, setHistExpanded] = useState(() => new Set());
  // Cache payload penuh per row-id (dimuat on-demand saat baris di-expand,
  // dipakai utk diff isi/konten — bukan cuma angka KPI).
  const [histDetail, setHistDetail]         = useState({}); // { [rowId]: payload|null }
  const [histDetailLoading, setHistDetailLoading] = useState(() => new Set());
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(false);
  const [err, setErr]           = useState('');
  const [searchParams] = useSearchParams();
  const [tab, setTab]           = useState(() => {
    const p = searchParams.get('tab');
    return BASIS_TABS.includes(p) ? p : 'system';
  });
  useTabDeepLink('sap_basis_mon', (p) => BASIS_TABS.includes(p), setTab);
  const [dump, setDump]         = useState(null); // { loading, key, text, readable, error, title }
  const [dumpRaw, setDumpRaw]   = useState(false);
  const isMobile = useIsMobile();
  const [mrow, setMrow] = useState(null); // { title, fields } → bottom-sheet baris tabel (mobile)
  const [logonQ, setLogonQ] = useState(''); // filter user/terminal/IP di tab User Logon
  const [sess, setSess] = useState(null);   // modal sesi/mode per user (ala SM04)

  // Klik baris di tab Logged On → daftar sesi/mode user tsb via FM TH_USER_LIST.
  const openSessions = useCallback(async (r) => {
    const bname = r.BNAME;
    setSess({ loading: true, bname, usr41: [] });
    try {
      const d = await api(`/api/sap/user-sessions?connectionId=${encodeURIComponent(connId)}&bname=${encodeURIComponent(bname)}`);
      setSess({ loading: false, bname, ...d });
    } catch (e) { setSess({ loading: false, bname, error: e.message || String(e) }); }
  }, [connId, api]);

  useEffect(() => {
    if (!sess) return;
    const onKey = (e) => { if (e.key === 'Escape') setSess(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sess]);

  const openDump = useCallback(async (r) => {
    const title = `${fmtDate(r.DATUM)} ${fmtTime(r.UZEIT)} · ${r.UNAME}`;
    setDumpRaw(false);
    setDump({ loading: true, title });
    try {
      const qs = new URLSearchParams({
        connectionId: connId, datum: r.DATUM || '', uzeit: r.UZEIT || '',
        uname: r.UNAME || '', mandt: r.MANDT || '', modno: r.MODNO || '', ahost: r.AHOST || '',
      }).toString();
      const d = await api(`/api/sap/dump-detail?${qs}`);
      setDump({ loading: false, title, text: d.text || '', readable: d.readable || [], segments: d.segments });
    } catch (e) { setDump({ loading: false, title, error: e.message || String(e) }); }
  }, [connId, api]);

  // ESC menutup modal detail dump
  useEffect(() => {
    if (!dump) return;
    const onKey = (e) => { if (e.key === 'Escape') setDump(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dump]);

  useEffect(() => {
    (async () => {
      try {
        const d = await api('/api/sap/connections');
        const list = Array.isArray(d) ? d : (d.connections || d.rows || []);
        setProfiles(list);
        let saved = null;
        try { saved = localStorage.getItem('sapBasisMonitor.connId'); } catch (_) {}
        const initial = (saved && list.some(p => String(p.id) === String(saved)))
          ? String(saved)
          : (list[0] ? String(list[0].id) : '');
        if (initial) setConnId(initial);
      } catch (e) { setErr(e.message || String(e)); }
    })();
  }, []); // eslint-disable-line

  // Ingat profil terakhir yang dipilih (per-browser)
  useEffect(() => {
    if (connId) { try { localStorage.setItem('sapBasisMonitor.connId', connId); } catch (_) {} }
  }, [connId]);

  // Saat profil dipilih → tampilkan snapshot tersimpan milik profil itu
  // (tanpa panggil SAP). Data profil sebelumnya dikosongkan dulu agar tidak
  // tertukar. Load/Refresh memanggil SAP live dan me-replace snapshot HANYA
  // untuk connection yang aktif (PK per connection_id).
  useEffect(() => {
    if (!connId) return;
    let alive = true;
    setData(null); setErr('');
    (async () => {
      try {
        const d = await api(`/api/sap/health/cached?connectionId=${encodeURIComponent(connId)}`);
        if (alive) { setData(d); setErr(''); }
      } catch (_) {
        // belum ada snapshot untuk profil ini → empty-state + hint
        if (alive) setErr(t('basis.noSavedSnapshotProfileYet'));
      }
    })();
    return () => { alive = false; };
  }, [connId]); // eslint-disable-line

  const load = useCallback(async () => {
    if (!connId) { setErr(t('basis.selectProfileFirst')); return; }
    setLoading(true); setErr('');
    try {
      const d = await api(`/api/sap/health?connectionId=${encodeURIComponent(connId)}&jobsLookbackDays=${encodeURIComponent(lookback)}`);
      setData(d);
    } catch (e) { setErr(e.message || String(e)); setData(null); }
    finally { setLoading(false); }
  }, [connId, lookback, id]); // eslint-disable-line

  // ── Status data User Usage tersimpan (lokal, bukan panggilan SAP) ───────────
  // Dimuat terpisah dari health supaya bisa di-refresh langsung setelah fetch.
  const [usage, setUsage] = useState(null);   // hasil /api/sap/usr-utl/summary
  const [usageErr, setUsageErr] = useState('');
  const loadUsage = useCallback(async (cid) => {
    const target = cid || connId;
    if (!target) { setUsage(null); return; }
    try {
      const d = await api(`/api/sap/usr-utl/summary?connectionId=${encodeURIComponent(target)}`);
      setUsage(d); setUsageErr('');
    } catch (e) { setUsage(null); setUsageErr(e.message || String(e)); }
  }, [connId, api]);

  useEffect(() => { setUsage(null); setUsageErr(''); if (connId) loadUsage(connId); }, [connId]); // eslint-disable-line


  // ── Histori KPI (audit trail, Opsi C) ───────────────────────────────────────
  const loadHistory = useCallback(async () => {
    if (!connId) return;
    setHistLoading(true); setHistErr('');
    try {
      const qs = `connectionId=${encodeURIComponent(connId)}&limit=200`
        + (histFrom ? `&from=${encodeURIComponent(histFrom)}` : '')
        + (histTo ? `&to=${encodeURIComponent(histTo)}` : '');
      const d = await api(`/api/sap/health/history?${qs}`);
      setHistRows(d.rows || []); setHistBaseline(d.baseline || null); setHistExpanded(new Set());
    } catch (e) { setHistErr(e.message || String(e)); setHistRows([]); setHistBaseline(null); }
    finally { setHistLoading(false); }
  }, [connId, histFrom, histTo]); // eslint-disable-line

  useEffect(() => { if (histOpen && connId) loadHistory(); }, [histOpen, connId]); // eslint-disable-line

  // Ambil payload penuh 1 baris histori (cache — tidak fetch ulang kalau sudah ada).
  const loadHistDetail = useCallback(async (rowId) => {
    if (!rowId || histDetail[rowId] !== undefined || histDetailLoading.has(rowId)) return;
    setHistDetailLoading(prev => new Set(prev).add(rowId));
    try {
      const d = await api(`/api/sap/health/history/detail?id=${encodeURIComponent(rowId)}`);
      setHistDetail(prev => ({ ...prev, [rowId]: d.payload || null }));
    } catch (e) {
      setHistDetail(prev => ({ ...prev, [rowId]: null }));
    } finally {
      setHistDetailLoading(prev => { const n = new Set(prev); n.delete(rowId); return n; });
    }
  }, [api, histDetail, histDetailLoading]);

  const toggleHistRow = (rid, prevRid) => {
    setHistExpanded(prev => {
      const next = new Set(prev);
      if (next.has(rid)) next.delete(rid); else next.add(rid);
      return next;
    });
    loadHistDetail(rid);
    if (prevRid) loadHistDetail(prevRid);
  };

  // ── Export data User Usage ke Excel ───────────────────────────────────────
  // Workbook dibuat di backend (xlsx-js-style) supaya baris detail tidak perlu
  // dikirim ke browser dulu. Pola unduh blob sama dengan TransportRequest.jsx.
  const [uuExporting, setUuExporting] = useState(false);
  const exportUsage = useCallback(async () => {
    if (!connId || uuExporting) return;
    setUuExporting(true);
    try {
      const res = await axiosApi.get('/api/sap/usr-utl/export',
        { params: { connectionId: connId }, responseType: 'blob' });

      // Backend mengirim JSON bila gagal, walau responseType blob → baca ulang.
      if (res.data?.type === 'application/json') {
        const txt = await res.data.text();
        throw new Error(JSON.parse(txt).error || 'Export gagal.');
      }

      const cd = res.headers['content-disposition'] || '';
      const m = cd.match(/filename="([^"]+)"/);
      const name = m ? m[1] : `SAP_UserUsage_${new Date().toISOString().slice(0, 10)}.xlsx`;

      const url = window.URL.createObjectURL(new Blob([res.data],
        { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const a = document.createElement('a');
      a.href = url; a.download = name;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      let msg = e.message;
      if (e.response?.data instanceof Blob) {
        try { msg = JSON.parse(await e.response.data.text()).error || msg; } catch (_) {}
      } else if (e.response?.data?.error) {
        msg = e.response.data.error;
      }
      alert('Export gagal: ' + msg);
    } finally {
      setUuExporting(false);
    }
  }, [connId, uuExporting]);

  // ── Fetch user utilization (raw) via ZRFC_USR_UTL ───────────────────────────
  const todayISO = new Date().toISOString().slice(0, 10);
  const [uuModal, setUuModal] = useState(false);
  const [uuBusy, setUuBusy]   = useState(false);
  const [uuMsg, setUuMsg]     = useState(null);   // { text, type }
  const [uuForm, setUuForm]   = useState({ uname: 'PG*', department: '', dateFrom: todayISO.slice(0, 8) + '01', dateTo: todayISO, soapPath: '/sap/bc/soap/rfc', mode: 'replace' });
  const runUuFetch = useCallback(async () => {
    if (!connId) { setUuMsg({ text: t('basis.selectProfileFirst'), type: 'error' }); return; }
    setUuBusy(true); setUuMsg(null);
    try {
      const r = await api('/api/sap/usr-utl/fetch', {
        method: 'POST',
        body: JSON.stringify({ ...uuForm, connectionId: Number(connId) }),
      });
      setUuMsg({ text: r.message, type: 'success' });
      // Segarkan kartu "User Usage" & buka tabnya supaya hasil fetch langsung terlihat.
      await loadUsage();
      setTab('usage');
    } catch (e) { setUuMsg({ text: e.message || String(e), type: 'error' }); }
    finally { setUuBusy(false); }
  }, [connId, uuForm, id]); // eslint-disable-line

  const jobGroups = useMemo(() => {
    const rows = data?.failedJobs?.rows || [];
    const map = {};
    rows.forEach(r => {
      const k = r.JOBNAME || '?';
      if (!map[k]) map[k] = { jobname: k, count: 0, last: '', sd: '', st: '', users: new Set() };
      const g = map[k]; g.count++;
      const key = (r.STRTDATE || '') + (r.STRTTIME || '');
      if (key > g.last) { g.last = key; g.sd = r.STRTDATE; g.st = r.STRTTIME; }
      if (r.SDLUNAME) g.users.add(r.SDLUNAME);
    });
    return Object.values(map).sort((a, b) => b.count - a.count);
  }, [data]);

  const si = data?.systemInfo, wp = data?.workProcesses, fj = data?.failedJobs;
  const lu = data?.lockedUsers, sd = data?.shortDumps, le = data?.lockEntries;
  const ue = data?.updateErrors, dk = data?.disk, sl = data?.sybaseLog, mc = data?.sybaseMonconfig, sps = data?.sybaseSpace;
  const lo = data?.loggedOnUsers, ml = data?.multiLogon;

  // Umur data usage (hari) → dipakai untuk warna kartu: makin lama, makin "perlu di-fetch".
  const usageAgeDays = useMemo(() => {
    if (!usage || usage.empty || !usage.lastFetchAt) return null;
    const t = new Date(usage.lastFetchAt).getTime();
    if (!t) return null;
    return Math.max(0, Math.floor((Date.now() - t) / 86400000));
  }, [usage]);
  const usageTone = usage == null ? '#94a3b8'
    : usage.empty ? '#94a3b8'
    : usageAgeDays == null ? '#0891b2'
    : usageAgeDays <= 7 ? '#16a34a'
    : usageAgeDays <= 30 ? '#d97706' : '#dc2626';

  // Daftar sesi logon setelah difilter (user / terminal / IP)
  const logonRows = useMemo(() => {
    const rows = lo?.rows || [];
    const q = logonQ.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r => [r.BNAME, r.TERMINAL, r.IP].some(v => String(v || '').toLowerCase().includes(q)));
  }, [lo, logonQ]);

  // Client Copy History (SCC3 / CCCFLOW) — dimuat lazy saat tab dibuka.
  const [cc, setCc] = useState({ data: null, loading: false, error: '', conn: '' });
  const loadClientCopy = useCallback(async () => {
    if (!connId) return;
    setCc(p => ({ ...p, loading: true, error: '', conn: connId, data: p.conn === connId ? p.data : null }));
    try {
      const d = await api(`/api/sap/basis/client-copy?connectionId=${encodeURIComponent(connId)}`);
      setCc({ data: d, loading: false, error: '', conn: connId });
    } catch (e) { setCc(p => ({ ...p, loading: false, error: e.message })); }
  }, [connId, api]);
  useEffect(() => {
    if (tab === 'clientCopy' && connId && cc.conn !== connId && !cc.loading) loadClientCopy();
  }, [tab, connId]); // eslint-disable-line

  // KPI: nilai + warna + status error per seksi
  const kpis = [
    { key: 'system', label: t('basis.system'), icon: Server, val: si?.ok ? (si.sid || 'OK') : '—', tone: si?.ok ? '#16a34a' : '#dc2626', sub: si?.ok ? si.dbSystem : (id ? 'error' : 'error') },
    { key: 'wp', label: 'Work Proc', icon: Cpu, val: wp?.ok ? `${wp.busy}/${wp.count}` : '—', tone: wp?.ok ? (wp.busy >= wp.count && wp.count ? '#dc2626' : '#2563eb') : '#dc2626', sub: t('basis.busyTotal') },
    { key: 'jobs', label: t('basis.failedJobs'), icon: AlertTriangle, val: fj?.ok ? `${fmt(fj.count)}${fj.count >= 500 ? '+' : ''}` : '—', tone: fj?.ok ? (fj.count ? '#dc2626' : '#16a34a') : '#dc2626' },
    { key: 'locked', label: t('basis.locked'), icon: Lock, val: lu?.ok ? fmt(lu.count) : '—', tone: lu?.ok ? (lu.count ? '#d97706' : '#16a34a') : '#dc2626' },
    // SM04: user unik yang sedang logon / total sesi aktif (sumber tabel USR41)
    { key: 'logon', label: t('basis.logged'), icon: Users, val: lo?.ok ? `${fmt(lo.users)}/${fmt(lo.count)}` : (lo ? '—' : '·'), tone: lo?.ok ? '#0891b2' : (lo ? '#dc2626' : '#94a3b8') },
    { key: 'multilogon', label: 'Multi Logon', icon: UserX, val: ml?.ok ? fmt(ml.count) : (ml ? '—' : '·'), tone: ml?.ok ? (ml.count ? '#d97706' : '#16a34a') : (ml ? '#dc2626' : '#94a3b8') },
    // Data lokal hasil "Fetch User Usage" (tabel sap_usr_utl_raw), bukan panggilan SAP.
    { key: 'usage', label: 'User Usage', icon: BarChart3, val: usage ? (usage.empty ? '0' : fmt(usage.users)) : '·', tone: usageTone },
    { key: 'dumps', label: 'Dumps', icon: Bug, val: sd?.ok ? fmt(sd.count) : '—', tone: sd?.ok ? (sd.count ? '#dc2626' : '#16a34a') : '#dc2626' },
    { key: 'locks', label: 'Lock SM12', icon: KeyRound, val: le?.ok ? fmt(le.count) : '—', tone: le?.ok ? (le.count ? '#d97706' : '#16a34a') : '#dc2626' },
    { key: 'updates', label: 'Update Err', icon: Database, val: ue?.ok ? fmt(ue.count) : (ue ? '—' : '·'), tone: ue?.ok ? (ue.count ? '#dc2626' : '#16a34a') : '#94a3b8' },
    { key: 'disk', label: 'Disk', icon: HardDrive, val: dk?.ok ? 'OK' : (dk ? '—' : '·'), tone: dk?.ok ? '#16a34a' : '#94a3b8' },
    { key: 'sybaseLog', label: 'Sybase Log', icon: FileText, val: sl?.ok ? (sl.count ? fmt(sl.count) : 'OK') : (sl ? '—' : '·'), tone: sl?.ok ? '#0891b2' : '#94a3b8' },
    { key: 'aseConfig', label: 'ASE Config', icon: Database, val: mc?.ok ? String((mc.rows || []).filter(function (r) { return r.name !== 'max memory' && ((r.pctAct != null && Number(r.pctAct) >= 80) || Number(r.reuseCnt || 0) > 0); }).length) : (mc ? '—' : '··'), tone: mc?.ok ? ((mc.rows || []).some(function (r) { return r.name !== 'max memory' && ((r.pctAct != null && Number(r.pctAct) >= 95) || Number(r.reuseCnt || 0) > 0); }) ? '#dc2626' : '#0891b2') : '#94a3b8' },
    { key: 'clientCopy', label: 'Client Copy', icon: Copy, val: (cc.data && cc.conn === connId) ? fmt(cc.data.summary?.total) : (cc.loading ? '…' : '·'), tone: (cc.data && cc.conn === connId) ? (cc.data.summary?.notOk ? '#d97706' : '#16a34a') : '#94a3b8' },
    { key: 'dbSpace', label: 'DB Space', icon: HardDrive, val: sps?.ok ? String((sps.segments || []).filter(function (r) { return Number(r.usedPct || 0) >= 85; }).length) : (sps ? '—' : '··'), tone: sps?.ok ? ((sps.segments || []).some(function (r) { return Number(r.usedPct || 0) >= 90; }) ? '#dc2626' : '#0891b2') : '#94a3b8' },
  ];

  // Tanpa snapshot health, satu-satunya seksi yang punya isi adalah User Usage
  // (sumbernya lokal) → panel dipaksa ke situ agar tidak muncul error seksi kosong.
  const shown = (data || tab === 'clientCopy') ? tab : 'usage';

  // ── AI page-context (Opsi B): ringkasan health live untuk AI Assistant ──────
  // Set lengkap (Opsi C): count + detail per-seksi ter-clamp, agar AI bisa jawab
  // beragam pertanyaan ("dump apa saja", "WP sibuk siapa", "user terkunci sampai
  // kapan"). Semua fakta dari data live; AI hanya membacakan (tak mengarang).
  const aiSummary = useMemo(() => {
    if (!data) return null;
    const conn = data.connection || {};
    const stat = (x) => (x == null ? 'tidak diambil' : x.ok === false ? 'ERROR/tak tersedia' : null);
    const L = [];

    L.push(`SAP Basis Monitor — health snapshot (real-time).`);
    if (si?.ok) L.push(`Sistem: SID ${si.sid || '?'}, host ${si.host || conn.host || '?'}, DB host ${si.dbHost || '?'}, release ${si.release || '?'}, kernel ${si.kernel || '?'}, DB ${si.dbSystem || '?'}, OS ${si.os || '?'}, IP ${si.ipAddr || '?'}, TZ ${si.timezone || '?'}. Client ${conn.client || '?'}.`);
    else L.push(`Sistem: ${stat(si) || '-'} (host ${conn.host || '?'}, client ${conn.client || '?'}).`);
    L.push(`Rentang job gagal: ${lookback} hari. Data diambil: ${data.generatedAt || '-'}.`);

    // Ringkasan status semua seksi
    L.push('STATUS RINGKAS:');
    L.push(`- Work process: ${wp?.ok ? `${wp.busy}/${wp.count} sibuk${wp.count && wp.busy >= wp.count ? ' (PENUH!)' : ''}` : (stat(wp) || '-')}`);
    L.push(`- Job gagal (${lookback}h): ${fj?.ok ? `${fj.count}${fj.count >= 500 ? '+ (capped 500)' : ''}` : (stat(fj) || '-')}`);
    L.push(`- User terkunci: ${lu?.ok ? lu.count : (stat(lu) || '-')}`);
    L.push(`- Short dumps: ${sd?.ok ? sd.count : (stat(sd) || '-')}`);
    L.push(`- Lock entries (SM12): ${le?.ok ? le.count : (stat(le) || '-')}`);
    L.push(`- Update errors (SM13): ${ue?.ok ? ue.count : (stat(ue) || '-')}`);
    L.push(`- Disk/ASE space: ${dk?.ok ? `OK (${(dk.rows || []).length} baris)` : (stat(dk) || '-')}`);
    L.push(`- Sybase errorlog: ${sl?.ok ? `${sl.count || 0} baris` : (stat(sl) || '-')}`);

    // WP by type + daftar WP sibuk
    if (wp?.ok) {
      const byType = Object.entries(wp.byType || {}).map(([t, n]) => `${t}: ${n}`).join(', ');
      if (byType) L.push(`WORK PROCESS per tipe: ${byType}.`);
      const busyRows = (wp.rows || []).filter(r => r.WP_STATUS && !/wait/i.test(r.WP_STATUS)).slice(0, 100)
        .map(r => `#${r.WP_INDEX ?? r.WP_NO} ${r.WP_TYP || '?'} ${r.WP_STATUS || ''} user:${r.WP_BNAME || '-'} aksi:${r.WP_ACTION || '-'} tabel:${r.WP_TABLE || '-'} elapsed:${r.WP_ELTIME || '-'}`);
      if (busyRows.length) L.push(`WP AKTIF/SIBUK: ${busyRows.join(' | ')}`);
    }

    // Job gagal: dikelompokkan per job (nama, jumlah, terakhir, user)
    if (jobGroups && jobGroups.length) {
      const jl = jobGroups.slice(0, 200).map(g => `${g.jobname} (${g.count}x, user:${[...g.users].join('/') || '-'})`);
      L.push(`JOB GAGAL per nama (${jobGroups.length} job unik): ${jl.join(', ')}${jobGroups.length > 200 ? ', …' : ''}`);
    }

    // User terkunci: nama, alasan, uflag, tipe, valid-to, last logon
    if (lu?.ok && (lu.rows || []).length) {
      const ll = lu.rows.slice(0, 200).map(r => `${r.BNAME} (alasan:${r.LOCK_REASON || '-'}, UFLAG:${r.UFLAG || '-'}, tipe:${r.USTYP || '-'}, valid s/d:${r.GLTGB || '-'}, login terakhir:${r.TRDAT || '-'})`);
      L.push(`USER TERKUNCI: ${ll.join('; ')}${lu.rows.length > 200 ? ` …(+${lu.rows.length - 200})` : ''}`);
    }

    // Short dumps: waktu, user, client, runtime error, program
    if (sd?.ok && (sd.rows || []).length) {
      const dl = sd.rows.slice(0, 200).map(r => `${r.DATUM || ''} ${r.UZEIT || ''} user:${r.UNAME || '-'} cl:${r.MANDT || '-'} err:${r.RUNTIME_ERROR || '-'} prog:${r.PROGRAM || '-'}`);
      L.push(`SHORT DUMPS: ${dl.join(' | ')}${sd.rows.length > 200 ? ` …(+${sd.rows.length - 200})` : ''}`);
      // agregasi per RUNTIME_ERROR (jenis dump terbanyak)
      const byErr = {};
      sd.rows.forEach(r => { const e = r.RUNTIME_ERROR || '(?)'; byErr[e] = (byErr[e] || 0) + 1; });
      const errTop = Object.entries(byErr).sort((a, b) => b[1] - a[1]).map(([e, n]) => `${e}: ${n}`);
      if (errTop.length) L.push(`DUMP per jenis error: ${errTop.join(', ')}.`);
    }

    // Lock entries SM12
    if (le?.ok && (le.rows || []).length) {
      const el = le.rows.slice(0, 200).map(r => `${r.GUNAME || '-'} obj:${r.GNAME || '-'} mode:${r.GMODE || '-'} host:${r.GTHOST || '-'} ${r.GTDATE || ''} ${r.GTTIME || ''} cl:${r.GCLIENT || '-'}`);
      L.push(`LOCK ENTRIES (SM12): ${el.join('; ')}${le.rows.length > 200 ? ` …(+${le.rows.length - 200})` : ''}`);
    }

    // Update errors SM13 (kolom dinamis → ringkas JSON per baris)
    if (ue?.ok && (ue.rows || []).length) {
      const ul = ue.rows.slice(0, 200).map(r => Object.entries(r).map(([k, v]) => `${k}:${v}`).join(' '));
      L.push(`UPDATE ERRORS (SM13): ${ul.join(' | ')}${ue.rows.length > 200 ? ` …(+${ue.rows.length - 200})` : ''}`);
    }

    // Disk/ASE space (kolom dinamis)
    if (dk?.ok && (dk.rows || []).length) {
      const dl = dk.rows.slice(0, 200).map(r => Object.entries(r).map(([k, v]) => `${k}:${v}`).join(' '));
      L.push(`DISK/ASE SPACE: ${dl.join(' | ')}${dk.rows.length > 200 ? ` …(+${dk.rows.length - 200})` : ''}`);
    }

    return L.join('\n');
  }, [data, si, wp, fj, lu, sd, le, ue, dk, sl, lookback, jobGroups]);

  // [Text-to-SQL] Pendekatan AI halaman ini berbasis SQL (datasource
  // 'sap_basis_mon' di ai_sql_sources → tabel sap_basis_snapshot &
  // sap_usr_utl_raw). Context statis besar tidak dikirim lagi; cukup catatan
  // singkat sebagai fallback bila mode SQL belum aktif.
  void aiSummary; // ringkasan statis lama — sengaja tidak dipakai lagi
  const aiSqlNote = useMemo(() => [
    'Mode AI halaman ini: text-to-SQL.',
    'Data health tersimpan di tabel sap_basis_snapshot (1 baris per profil, kolom payload = JSON hasil health; pakai json_extract).',
    "Key payload: systemInfo, workProcesses, failedJobs, lockedUsers, shortDumps, lockEntries, updateErrors, disk, sybaseLog, loggedOnUsers (SM04 dari USR41: count=sesi, users=user unik, rows[].BNAME/TERMINAL/IP), multiLogon (rows[].BNAME/SESSIONS/TERMINALS/IPS).",
    'Data user utilization mentah tersimpan di tabel sap_usr_utl_raw (1 baris per sesi login; algdur = HH:MM:SS).',
    `Profil aktif saat ini: connection_id=${connId || '-'}. Snapshot terakhir: ${data?.generatedAt || '-'}.`,
  ].join(' '), [connId, data]);

  usePageAIContext('sap_basis_mon', 'SAP Basis Monitor', aiSqlNote);

  return (
    <div style={{ padding: '1.5rem', maxWidth: 1200, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 4 }}>
        <span style={{ display: 'inline-flex', width: 38, height: 38, borderRadius: 10, background: '#14b8a614', alignItems: 'center', justifyContent: 'center' }}><Activity size={21} color="#0d9488" /></span>
        <div>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#0f172a' }}>SAP Basis Monitor</h1>
          <p style={{ margin: 0, fontSize: 12.5, color: '#64748b' }}>{t('basis.sapSystemHealthGlance')}</p>
        </div>
      </div>
      {/* Kontrol */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: '14px 18px', margin: '14px 0 16px' }}>
        <div style={{ flex: '1 1 280px' }}>
          <label style={{ display: 'block', fontSize: 12, color: '#64748b', marginBottom: 5, fontWeight: 600 }}>{t('basis.sapSystemProfile')}</label>
          <select value={connId} onChange={(e) => setConnId(e.target.value)} style={{ width: '100%', padding: '9px 11px', border: '1px solid #e2e8f0', borderRadius: 9, fontSize: 13, background: '#fff', outline: 'none' }}>
            <option value="">{t('basis.selectProfile')}</option>
            {profiles.map(p => <option key={p.id} value={p.id}>{(p.sid || '?')} · {p.landscape}/{p.environment} · {p.host} (cl.{p.client})</option>)}
          </select>
        </div>
        <div style={{ width: 130 }}>
          <label style={{ display: 'block', fontSize: 12, color: '#64748b', marginBottom: 5, fontWeight: 600 }}>{t('basis.rangeDays')}</label>
          <input type="number" min={0} max={30} value={lookback} onChange={(e) => setLookback(e.target.value)} style={{ width: '100%', padding: '9px 11px', border: '1px solid #e2e8f0', borderRadius: 9, fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
        </div>
        <button onClick={load} disabled={loading} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: loading ? '#93c5fd' : '#0d9488', color: '#fff', border: 'none', borderRadius: 9, padding: '10px 18px', fontSize: 13, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer' }}>
          <RefreshCw size={15} style={loading ? { animation: 'spin 1s linear infinite' } : undefined} />
          {loading ? (t('basis.loading')) : (t('basis.loadRefresh'))}
        </button>
        <button onClick={() => { setUuMsg(null); setUuModal(true); }} disabled={!connId} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: !connId ? '#cbd5e1' : '#2563eb', color: '#fff', border: 'none', borderRadius: 9, padding: '10px 18px', fontSize: 13, fontWeight: 600, cursor: !connId ? 'not-allowed' : 'pointer' }}>
          📡 {id ? 'Fetch User Usage' : 'Fetch User Usage'}
        </button>
        {user?.role === 'admin' && (
          <button onClick={() => setCronOpen((p) => !p)} disabled={!connId} title={connId ? t('cron.title') : t('basis.selectProfile')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: !connId ? '#cbd5e1' : (cronOpen ? '#0f172a' : '#f1f5f9'), color: !connId ? '#fff' : (cronOpen ? '#fff' : '#334155'), border: '1px solid #e2e8f0', borderRadius: 9, padding: '10px 18px', fontSize: 13, fontWeight: 600, cursor: !connId ? 'not-allowed' : 'pointer' }}>
            🕑 {t('cron.title')}
          </button>
        )}
        <button onClick={() => setHistOpen((p) => !p)} disabled={!connId} title={connId ? t('basis.historyTitle') : t('basis.selectProfile')}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: !connId ? '#cbd5e1' : (histOpen ? '#0f172a' : '#f1f5f9'), color: !connId ? '#fff' : (histOpen ? '#fff' : '#334155'), border: '1px solid #e2e8f0', borderRadius: 9, padding: '10px 18px', fontSize: 13, fontWeight: 600, cursor: !connId ? 'not-allowed' : 'pointer' }}>
          📜 {t('basis.historyTitle')}
        </button>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
      {/* Jadwal histori KPI (Opsi B, diff-only) — PER PROFIL SAP yang sedang dipilih
          (jobKey = sap_basis_health_history_<connId>). Ganti profil → panel ikut
          menampilkan jadwal milik profil itu, bukan jadwal global. */}
      {user?.role === 'admin' && cronOpen && connId && (
        <CronScheduler key={connId} api={api} jobKey={`sap_basis_health_history_${connId}`}
          runConfirm={t('basis.historyCronRunConfirm')} />
      )}
        {user?.role === 'admin' && cronOpen && (
          <div style={{ marginTop: 4 }}><AseJobsPanel /></div>
        )}

      {/* Histori KPI — audit trail (Opsi C): tabel + filter tanggal + baris
          bisa diklik utk lihat narasi perubahan vs baris sebelumnya. */}
      {histOpen && connId && (
        <div style={{ border: '1px solid #e2e8f0', borderRadius: 14, padding: 16, marginBottom: 16, background: '#fff' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>📜 {t('basis.historyTitle')}</span>
            <Badge text={t('basis.historyDiffOnlyNote')} color="#64748b" />
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end', marginBottom: 14 }}>
            <div>
              <label style={{ display: 'block', fontSize: 11, color: '#64748b', marginBottom: 4, fontWeight: 600 }}>{t('basis.historyFrom')}</label>
              <input type="date" value={histFrom} onChange={e => setHistFrom(e.target.value)}
                style={{ padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13 }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, color: '#64748b', marginBottom: 4, fontWeight: 600 }}>{t('basis.historyTo')}</label>
              <input type="date" value={histTo} onChange={e => setHistTo(e.target.value)}
                style={{ padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13 }} />
            </div>
            <button onClick={loadHistory} disabled={histLoading} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: histLoading ? '#93c5fd' : '#0d9488', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: histLoading ? 'not-allowed' : 'pointer' }}>
              <RefreshCw size={13} style={histLoading ? { animation: 'spin 1s linear infinite' } : undefined} />
              {histLoading ? t('basis.loading') : t('basis.historyLoad')}
            </button>
            <span style={{ fontSize: 12, color: '#94a3b8' }}>{t('basis.historyRowCount', { n: histRows.length })}</span>
          </div>

          {histErr && <Alert msg={histErr} type="error" onClose={() => setHistErr('')} />}

          {!histLoading && !histErr && histRows.length === 0 && (
            <Empty text={t('basis.historyEmpty')} />
          )}

          {histRows.length > 0 && (
            <div style={{ overflowX: 'auto', border: '1px solid #f1f5f9', borderRadius: 10 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <Th w={30}></Th>
                    <Th>{t('basis.historyRecordedAt')}</Th>
                    <Th w={260}>{t('basis.historyChanges')}</Th>
                    <Th>{t('basis.historyRecordedBy')}</Th>
                    {HIST_FIELDS.map(f => <Th key={f.label}>{f.label}</Th>)}
                  </tr>
                </thead>
                <tbody>
                  {histRows.map((r, i) => {
                    const prev = i < histRows.length - 1 ? histRows[i + 1] : histBaseline;
                    const expanded = histExpanded.has(r.id);
                    const changedFields = HIST_FIELDS.filter(f => histFieldChanged(f, r, prev));
                    return (
                      <React.Fragment key={r.id}>
                        <tr onClick={() => toggleHistRow(r.id, prev?.id)} style={{ cursor: 'pointer' }}>
                          <Td>{expanded ? '▾' : '▸'}</Td>
                          <Td mono>{new Date(r.recorded_at).toLocaleString()}</Td>
                          <td style={{ padding: '7px 12px', borderBottom: '1px solid #f1f5f9', fontSize: 12 }}>
                            {!prev ? (
                              <Badge text={t('basis.historyBaselineShort')} color="#64748b" />
                            ) : changedFields.length === 0 ? (
                              <span style={{ color: '#94a3b8' }}>—</span>
                            ) : (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                {changedFields.map(f => <Badge key={f.label} text={f.label} color="#d97706" />)}
                              </div>
                            )}
                          </td>
                          <Td>{r.recorded_by || '—'}</Td>
                          {HIST_FIELDS.map(f => {
                            const changed = histFieldChanged(f, r, prev);
                            return (
                              <Td key={f.label}>
                                <span style={changed ? { background: '#fef3c7', color: '#92400e', borderRadius: 6, padding: '2px 6px', fontWeight: 700 } : undefined}>
                                  {f.fmt(r)}
                                </span>
                              </Td>
                            );
                          })}
                        </tr>
                        {expanded && (
                          <tr style={{ background: '#f8fafc' }}>
                            <td colSpan={4 + HIST_FIELDS.length} style={{ background: '#f8fafc', borderBottom: '1px solid #f1f5f9', padding: '10px 16px', fontSize: 12.5, color: '#334155' }}>
                              {!prev ? (
                                <span>{t('basis.historyBaseline')}</span>
                              ) : changedFields.length === 0 ? (
                                <span>{t('basis.historyNoChange')}</span>
                              ) : (
                                <>
                                  <span>
                                    {t('basis.historyChangeIntro')}{' '}
                                    {changedFields.map((f, idx) => (
                                      <span key={f.label} style={{ marginRight: 10 }}>
                                        <b>{f.label}</b>: {f.fmt(prev)} → <b>{f.fmt(r)}</b>{idx < changedFields.length - 1 ? ',' : ''}
                                      </span>
                                    ))}
                                  </span>
                                  <HistDetailDiff t={t} changedFields={changedFields}
                                    curPayload={histDetail[r.id]} prevPayload={prev ? histDetail[prev.id] : null}
                                    loadingCur={histDetailLoading.has(r.id)} loadingPrev={!!prev && histDetailLoading.has(prev.id)} />
                                </>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Modal Fetch User Utilization (ZRFC_USR_UTL) — data mentah disimpan ke sap_usr_utl_raw */}
      {uuModal && (() => {
        const lbl = { display: 'block', fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 4 };
        const inp = { width: '100%', padding: '9px 11px', border: '1px solid #e2e8f0', borderRadius: 9, fontSize: 13, outline: 'none', boxSizing: 'border-box', background: '#fff' };
        const activeProfile = profiles.find(px => String(px.id) === String(connId));
        return (
          <div onClick={() => !uuBusy && setUuModal(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 440, padding: '20px 22px', boxShadow: '0 20px 50px rgba(0,0,0,0.25)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
                <h3 style={{ margin: 0, fontSize: 15.5, fontWeight: 800, color: '#0f172a' }}>📡 Fetch User Utilization dari SAP (ZRFC_USR_UTL)</h3>
                <button onClick={() => !uuBusy && setUuModal(false)} style={{ border: 'none', background: 'none', fontSize: 18, cursor: 'pointer', color: '#94a3b8', lineHeight: 1 }}>×</button>
              </div>
              <div style={{ display: 'grid', gap: 12 }}>
                <div>
                  <label style={lbl}>{t('basis.connectionProfileActive')}</label>
                  <div style={{ ...inp, background: '#f8fafc', color: '#334155', fontWeight: 600 }}>
                    {activeProfile ? `${activeProfile.landscape}/${activeProfile.environment} · ${activeProfile.sid || activeProfile.host} (client ${activeProfile.client || '-'})` : '-'}
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div>
                    <label style={lbl}>{t('basis.date')}</label>
                    <input type="date" style={inp} value={uuForm.dateFrom} onChange={e => setUuForm(f => ({ ...f, dateFrom: e.target.value }))} />
                  </div>
                  <div>
                    <label style={lbl}>{t('basis.date2')}</label>
                    <input type="date" style={inp} value={uuForm.dateTo} onChange={e => setUuForm(f => ({ ...f, dateTo: e.target.value }))} />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div>
                    <label style={lbl}>User Pattern (UNAME)</label>
                    <input style={inp} value={uuForm.uname} placeholder="PG*" onChange={e => setUuForm(f => ({ ...f, uname: e.target.value }))} />
                  </div>
                  <div>
                    <label style={lbl}>Department ({t('basis.optional')})</label>
                    <input style={inp} value={uuForm.department} onChange={e => setUuForm(f => ({ ...f, department: e.target.value }))} />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div>
                    <label style={lbl}>SOAP Path</label>
                    <input style={inp} value={uuForm.soapPath} placeholder="/sap/bc/soap/rfc" onChange={e => setUuForm(f => ({ ...f, soapPath: e.target.value }))} />
                  </div>
                  <div>
                    <label style={lbl}>{t('basis.saveMode')}</label>
                    <select style={inp} value={uuForm.mode} onChange={e => setUuForm(f => ({ ...f, mode: e.target.value }))}>
                      <option value="replace">Replace (per source)</option>
                      <option value="append">Append / merge</option>
                    </select>
                  </div>
                </div>
                <div style={{ fontSize: 11, color: '#94a3b8' }}>
                  {t('basis.loginDetailRowsStoredAs')}
                </div>
                {uuMsg && (
                  <div style={{ padding: '9px 12px', borderRadius: 9, fontSize: 12.5, fontWeight: 600, background: uuMsg.type === 'success' ? '#dcfce7' : '#fee2e2', color: uuMsg.type === 'success' ? '#166534' : '#991b1b' }}>
                    {uuMsg.text}
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 2 }}>
                  <button onClick={() => setUuModal(false)} disabled={uuBusy} style={{ padding: '9px 16px', borderRadius: 9, border: '1px solid #e2e8f0', background: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#334155' }}>Batal</button>
                  <button onClick={runUuFetch} disabled={uuBusy} style={{ padding: '9px 16px', borderRadius: 9, border: 'none', background: uuBusy ? '#93c5fd' : '#2563eb', color: '#fff', fontSize: 13, fontWeight: 700, cursor: uuBusy ? 'not-allowed' : 'pointer' }}>
                    {uuBusy ? '⏳ Mengambil…' : '📡 Fetch & Simpan'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
      {err && <div style={{ marginBottom: 14 }}><SectionError msg={err} /></div>}
      {!data && !usage && !loading && !err && (
        <div style={{ padding: '3rem', textAlign: 'center', color: '#94a3b8' }}>
          <Database size={34} style={{ marginBottom: 10, opacity: 0.5 }} />
          <div style={{ fontSize: 14 }}>{t('basis.selectProfileClickLoadView')}</div>
        </div>
      )}
      {(data || usage) && (
        <>
          {data?.generatedAt && (
            <div style={{ fontSize: 12, color: '#94a3b8', margin: '0 0 12px' }}>
              {t('basis.updated')}: {new Date(data.generatedAt).toLocaleString()} · {data.connection?.host}:{data.connection?.port} (cl.{data.connection?.client})
              {data.cached && <span style={{ marginLeft: 8, padding: '2px 8px', borderRadius: 999, background: '#fef3c7', color: '#92400e', fontSize: 11, fontWeight: 700 }}>💾 {t('basis.savedSnapshot')}{data.cachedBy ? ` · ${data.cachedBy}` : ''}</span>}
            </div>
          )}

          {/* KPI strip (klik untuk pindah tab) */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(118px, 1fr))', gap: 10, marginBottom: 18 }}>
            {kpis.filter(k => BASIS_TABS.includes(k.key)).map(k => {
              const Icon = k.icon; const active = shown === k.key;
              return (
                <button key={k.key} onClick={() => setTab(k.key)} style={{
                  textAlign: 'left', cursor: 'pointer', background: active ? k.tone + '0f' : '#fff',
                  border: `1px solid ${active ? k.tone + '66' : '#e2e8f0'}`, borderRadius: 12, padding: '11px 13px',
                  display: 'flex', flexDirection: 'column', gap: 4, transition: 'all .12s',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#64748b', fontSize: 11, fontWeight: 600 }}>
                    <Icon size={13} color={k.tone} /> {k.label}
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: k.tone }}>{k.val}</div>
                </button>
              );
            })}
          </div>

          {/* Konten tab */}
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: 18, minHeight: 180 }}>
            {shown === 'clientCopy' && user?.role === 'admin' && ['Q01'].includes(String((profiles.find(px => String(px.id) === String(connId)) || {}).sid || '').toUpperCase()) && <ClientCopyPrepPanel sid={String((profiles.find(px => String(px.id) === String(connId)) || {}).sid).toUpperCase()} />}
            {shown === 'clientCopy' && <ClientCopyPanel data={cc.conn === connId ? cc.data : null} loading={cc.loading} error={cc.error} onReload={loadClientCopy} />}
            {shown === 'system' && (!si?.ok ? <SectionError msg={si?.error} /> : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10 }}>
                {[['SID', si.sid], ['Host', si.host], ['DB Host', si.dbHost], ['Release', si.release], ['Kernel', si.kernel], ['Database', si.dbSystem], ['OS', si.os], ['Machine', si.machine], ['IP', si.ipAddr], ['Timezone', si.timezone]].map(([k, v]) => (
                  <div key={k} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '9px 13px' }}>
                    <div style={{ fontSize: 10.5, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>{k}</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginTop: 2 }}>{v || '—'}</div>
                  </div>
                ))}
              </div>
            ))}

            {shown === 'wp' && (!wp?.ok ? <SectionError msg={wp?.error} /> : wp.count === 0 ? <Empty text={t('basis.noWpData')} /> : (
              <>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginBottom: 14 }}>
                  {Object.entries(wp.byType || {}).map(([t, n]) => <Badge key={t} text={`${t}: ${n}`} color={WP_TYPE_COLOR[t] || '#64748b'} />)}
                  <Badge text={`${fmt(wp.busy)} ${t('basis.busy')}`} color={wp.busy ? '#d97706' : '#16a34a'} />
                </div>
                {isMobile ? (
                  <CardList
                    items={wp.rows.map((r, i) => ({ __i: i, ...r }))}
                    keyOf={(r) => String(r.__i)}
                    title={(r) => `WP ${r.WP_INDEX ?? r.WP_NO} · ${r.WP_TYP || '?'}`}
                    badge={(r) => ({ label: r.WP_STATUS || '—', tone: /run|hold/i.test(r.WP_STATUS || '') ? 'amber' : 'slate' })}
                    subtitle={(r) => [r.WP_BNAME, r.WP_ACTION, r.WP_TABLE].filter(Boolean).join(' · ') || '—'}
                    onOpen={(r) => setMrow({
                      title: `WP ${r.WP_INDEX ?? r.WP_NO} · ${r.WP_TYP || '?'}`,
                      fields: [
                        ['PID', r.WP_PID], ['Status', r.WP_STATUS], ['User', r.WP_BNAME],
                        [t('basis.action'), r.WP_ACTION], ['Tabel', r.WP_TABLE], ['Elapsed', r.WP_ELTIME],
                      ].map(([label, value]) => ({ label, value })),
                    })}
                  />
                ) : (
                <Table head={<><Th w={50}>No</Th><Th w={55}>Type</Th><Th>PID</Th><Th>Status</Th><Th>User</Th><Th>{t('basis.action')}</Th><Th>Tabel</Th><Th>Elapsed</Th></>}>
                  {wp.rows.map((r, i) => (
                    <tr key={i}>
                      <Td mono>{r.WP_INDEX ?? r.WP_NO}</Td>
                      <Td><Badge text={r.WP_TYP || '?'} color={WP_TYPE_COLOR[r.WP_TYP] || '#64748b'} /></Td>
                      <Td mono>{r.WP_PID}</Td><Td>{r.WP_STATUS}</Td>
                      <Td title={r.WP_BNAME}>{r.WP_BNAME}</Td><Td title={r.WP_ACTION}>{r.WP_ACTION}</Td>
                      <Td mono title={r.WP_TABLE}>{r.WP_TABLE}</Td><Td mono>{r.WP_ELTIME}</Td>
                    </tr>
                  ))}
                </Table>
                )}
              </>
            ))}

            {shown === 'jobs' && (!fj?.ok ? <SectionError msg={fj?.error} /> : jobGroups.length === 0 ? <Empty text={t('basis.noCancelledJobs')} /> : (
              <>
                {fj.count >= 500 && <div style={{ fontSize: 12, color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 12px', marginBottom: 12 }}>{t('basis.showingFirst500EventsCapped')}</div>}
                {isMobile ? (
                  <CardList
                    items={jobGroups}
                    keyOf={(g) => g.jobname}
                    title={(g) => g.jobname}
                    badge={(g) => ({ label: fmt(g.count), tone: g.count >= 10 ? 'rose' : 'slate' })}
                    subtitle={(g) => `${fmtDate(g.sd)} ${fmtTime(g.st)} · ${[...g.users].join(', ') || '—'}`}
                    onOpen={(g) => setMrow({
                      title: g.jobname,
                      fields: [
                        { label: t('basis.count'), value: fmt(g.count) },
                        { label: t('basis.latest'), value: `${fmtDate(g.sd)} ${fmtTime(g.st)}` },
                        { label: t('basis.by'), value: [...g.users].join(', ') || '—' },
                      ],
                    })}
                  />
                ) : (
                <Table head={<><Th>{t('basis.jobName')}</Th><Th w={90}>{t('basis.count')}</Th><Th>{t('basis.latest')}</Th><Th>{t('basis.by')}</Th></>}>
                  {jobGroups.map(g => (
                    <tr key={g.jobname}>
                      <Td mono title={g.jobname}>{g.jobname}</Td>
                      <Td><Badge text={fmt(g.count)} color={g.count >= 10 ? '#dc2626' : '#64748b'} /></Td>
                      <Td>{fmtDate(g.sd)} {fmtTime(g.st)}</Td><Td>{[...g.users].join(', ') || '—'}</Td>
                    </tr>
                  ))}
                </Table>
                )}
              </>
            ))}

            {shown === 'locked' && (!lu?.ok ? <SectionError msg={lu?.error} /> : (lu.rows || []).length === 0 ? <Empty text={t('basis.noLockedUsers')} /> : (
              isMobile ? (
                <CardList
                  items={lu.rows.map((r, i) => ({ __i: i, ...r }))}
                  keyOf={(r) => String(r.__i)}
                  title={(r) => r.BNAME}
                  badge={(r) => ({ label: `UFLAG ${r.UFLAG}`, tone: 'amber', Icon: Lock })}
                  subtitle={(r) => [r.LOCK_REASON, fmtDate(r.GLTGB)].filter(Boolean).join(' · ') || '—'}
                  onOpen={(r) => setMrow({
                    title: r.BNAME,
                    fields: [
                      { label: t('basis.reason'), value: r.LOCK_REASON },
                      { label: 'UFLAG', value: r.UFLAG }, { label: 'Type', value: r.USTYP },
                      { label: t('basis.valid'), value: fmtDate(r.GLTGB) },
                      { label: t('basis.lastLogon'), value: fmtDate(r.TRDAT) },
                    ],
                  })}
                />
              ) : (
              <Table head={<><Th>User</Th><Th>{t('basis.reason')}</Th><Th w={70}>UFLAG</Th><Th w={70}>Type</Th><Th>{t('basis.valid')}</Th><Th>{t('basis.lastLogon')}</Th></>}>
                {lu.rows.map((r, i) => (
                  <tr key={i}><Td mono>{r.BNAME}</Td><Td title={r.LOCK_REASON}>{r.LOCK_REASON}</Td><Td mono>{r.UFLAG}</Td><Td>{r.USTYP}</Td><Td>{fmtDate(r.GLTGB)}</Td><Td>{fmtDate(r.TRDAT)}</Td></tr>
                ))}
              </Table>
              )
            ))}

            {/* SM04 — sesi logon aktif dari tabel USR41 (BNAME + TERMINAL + HOSTADDR→IP) */}
            {shown === 'logon' && (
              !lo ? <Pending text={t('basis.loggedUsersSm04ClickLoad')} />
              : !lo.ok ? <SectionError msg={lo.error} />
              : (lo.rows || []).length === 0 ? <Empty text={t('basis.noUsersCurrentlyLogged')} /> : (
                <>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, alignItems: 'center', marginBottom: 12 }}>
                    <Badge text={`${fmt(lo.users)} ${t('basis.users')}`} color="#0891b2" />
                    <Badge text={`${fmt(lo.count)} ${t('basis.sessions')}`} color="#2563eb" />
                    {ml?.ok && ml.count > 0 && <Badge text={`${fmt(ml.count)} multi logon`} color="#d97706" />}
                    {lo.service > 0 && <Badge text={`${fmt(lo.service)} ${t('basis.serviceUserSessions')}`} color="#94a3b8" />}
                    <input
                      value={logonQ}
                      onChange={(e) => setLogonQ(e.target.value)}
                      placeholder={t('basis.searchUserTerminalIp')}
                      style={{ marginLeft: 'auto', minWidth: 200, padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 9, fontSize: 12.5, outline: 'none' }}
                    />
                  </div>
                  {logonRows.length === 0 ? <Empty text={t('basis.noRowsMatchFilter')} /> : isMobile ? (
                    <CardList
                      items={logonRows.map((r, i) => ({ __i: i, ...r }))}
                      keyOf={(r) => String(r.__i)}
                      title={(r) => r.BNAME}
                      badge={(r) => ({ label: r.SESSIONS > 1 ? `${r.SESSIONS} ${t('basis.sessions')}` : (t('basis.1Session')), tone: r.SESSIONS > 1 ? 'amber' : 'slate', Icon: Users })}
                      subtitle={(r) => [r.TERMINAL, r.IP, fmtTime(r.LOGON_TIME)].filter(Boolean).join(' · ') || '—'}
                      onOpen={(r) => openSessions(r)}
                    />
                  ) : (
                    <Table head={<><Th>User</Th><Th w={70}>{t('basis.sessions2')}</Th><Th>Terminal</Th><Th>IP</Th><Th w={64}>Client</Th><Th>{t('basis.date3')}</Th><Th>{t('basis.time')}</Th></>}>
                      {logonRows.map((r, i) => (
                        <tr key={i} onClick={() => openSessions(r)} style={{ cursor: 'pointer' }}
                            onMouseEnter={e => { e.currentTarget.style.background = '#f8fafc'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = ''; }}>
                          <Td mono title={t('basis.clickViewUserSSessions')}>
                            {r.BNAME}
                            {r.SERVICE && <span style={{ marginLeft: 6, fontSize: 10, color: '#94a3b8', fontFamily: 'inherit' }}>({id ? 'service' : 'service'})</span>}
                          </Td>
                          <Td>{r.SESSIONS > 1 ? <Badge text={fmt(r.SESSIONS)} color="#d97706" /> : <span style={{ color: '#94a3b8' }}>1</span>}</Td>
                          <Td mono title={r.TERMINAL}>{r.TERMINAL || '—'}</Td>
                          <Td mono title={r.IP}>{r.IP || '—'}</Td>
                          <Td mono>{r.CLIENT}</Td>
                          <Td>{fmtDate(r.LOGON_DATE)}</Td>
                          <Td mono>{fmtTime(r.LOGON_TIME) || '—'}</Td>
                        </tr>
                      ))}
                    </Table>
                  )}
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 10 }}>
                    {id
                      ? `Sumber: tabel ${lo.table || 'USR41'} (sesi logon aktif). IP dari HOSTADDR; bila kosong, terminal dipakai sebagai fallback.`
                      : `Source: table ${lo.table || 'USR41'} (active logon sessions). IP from HOSTADDR; terminal used as fallback when empty.`}
                    {' '}{t('basis.clickRowViewUserS')}
                    {lo.fields?.length > 0 && (
                      <> {t('basis.fieldsRead')}: <span style={{ fontFamily: 'monospace' }}>{lo.fields.join(', ')}</span>.</>
                    )}
                  </div>
                </>
              )
            )}

            {/* Multi Logon — tiga konsep dipisah: sesi paralel aktif (KPI),
                whitelist RZ11, dan catatan tabel MLD (akumulatif, bukan KPI). */}
            {shown === 'multilogon' && (() => {
              if (!ml) return <Pending text={t('basis.multiLogonClickLoadRefresh')} />;
              if (!ml.ok) return <SectionError msg={ml.error} />;
              const wl = ml.allowedParam || {};

              // Penjelasan kriteria + sumber angka. Selalu tampil, termasuk saat 0.
              const criteria = (
                <div style={{ fontSize: 11.5, color: '#64748b', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 13px', marginTop: 14, lineHeight: 1.65 }}>
                  <div style={{ fontWeight: 700, color: '#475569', marginBottom: 3 }}>{t('basis.criteriaDataSources')}</div>
                  <div>
                    <b>{t('basis.multiLogonCard')}</b> = {id
                      ? <>user dengan lebih dari satu sesi aktif di <code>{lo?.table || 'USR41'}</code> saat snapshot diambil (sepadan dengan SM04). Sekarang: <b>{fmt(ml.count)}</b> user dari {fmt(lo?.users ?? 0)} user / {fmt(lo?.count ?? 0)} sesi.</>
                      : <>users with more than one active session in <code>{lo?.table || 'USR41'}</code> at snapshot time (comparable to SM04). Now: <b>{fmt(ml.count)}</b> of {fmt(lo?.users ?? 0)} users / {fmt(lo?.count ?? 0)} sessions.</>}
                  </div>
                  <div>
                    <b>RZ11</b> <code>login/multi_login_users</code> = {wl.ok
                      ? (id
                        ? <>whitelist user yang <i>diizinkan</i> multiple logon — <b>{fmt((ml.allowed || []).length)}</b> user (dibaca via FM {wl.fm}).</>
                        : <>whitelist of users <i>allowed</i> multiple logon — <b>{fmt((ml.allowed || []).length)}</b> users (read via FM {wl.fm}).</>)
                      : <span style={{ color: '#b45309' }}>{t('basis.unreadable')} — {(wl.tried || []).map(t => `${t.fm}: ${t.error || 'ok'}`).join(' · ') || '—'}</span>}
                  </div>
                  {ml.disableParam?.ok && (
                    <div><code>login/disable_multi_gui_login</code> = <b>{ml.disableParam.value}</b> {ml.disableParam.value === '0' ? (t('basis.multipleLogonNotRestricted')) : (t('basis.multipleLogonRestricted'))}</div>
                  )}
                  {(ml.mldTried || []).map((t, i) => (
                    <div key={i}>
                      <code>{t.table}</code> = {t.ok
                        ? (id
                          ? <>catatan kernel, <b>{fmt(t.rows)}</b> baris. Tabel ini <b>akumulatif</b> dan bukan whitelist RZ11 — karena itu tidak dipakai sebagai KPI.</>
                          : <>kernel record, <b>{fmt(t.rows)}</b> rows. This table is <b>cumulative</b> and is not the RZ11 whitelist — so it is not used for the KPI.</>)
                        : <span style={{ color: '#94a3b8' }}>{id ? 'tidak tersedia' : 'not available'} ({t.error})</span>}
                    </div>
                  ))}
                </div>
              );

              // User di tabel MLD yang TIDAK sedang punya sesi paralel.
              const mldOnly = (ml.mldOnly || []).length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <div style={{ fontSize: 11.5, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
                    {t('basis.listedMldtableButNotParallel', {
                      mldTable: ml.mldTable
                    })}
                    <span style={{ fontWeight: 500, textTransform: 'none', letterSpacing: 0, color: '#94a3b8' }}> · {fmt(ml.mldOnly.length)} user</span>
                  </div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 7 }}>
                    {t('basis.notCountedAsMultiLogon')}
                  </div>
                  <DynamicTable rows={ml.mldOnly.map(r => ({
                    User: r.BNAME,
                    [t('basis.activeSessions')]: r.SESSIONS || '—',
                    [t('basis.rz11Whitelist')]: wl.ok ? (r.ALLOWED ? (t('basis.yes')) : (t('basis.no'))) : '?',
                  }))} />
                </div>
              );

              if ((ml.rows || []).length === 0) return (
                <>
                  <Empty text={t('basis.noUsersParallelSessionsRight')} />
                  {criteria}
                  {mldOnly}
                </>
              );

              return (
                <>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginBottom: 12 }}>
                    <Badge text={`${fmt(ml.count)} ${t('basis.parallelSessions')}`} color={ml.count ? '#d97706' : '#16a34a'} />
                    {ml.violations != null && <Badge text={`${fmt(ml.violations)} ${t('basis.outsideRz11Whitelist')}`} color={ml.violations ? '#dc2626' : '#16a34a'} />}
                    {ml.multiHost > 0 && <Badge text={`${fmt(ml.multiHost)} ${t('basis.differentTerminals')}`} color="#dc2626" />}
                    {ml.mldRowCount > 0 && <Badge text={`${fmt(ml.mldRowCount)} ${t('basis.rows')} ${ml.mldTable}`} color="#7c3aed" />}
                  </div>
                  {isMobile ? (
                    <CardList
                      items={ml.rows.map((r, i) => ({ __i: i, ...r }))}
                      keyOf={(r) => String(r.__i)}
                      title={(r) => r.BNAME}
                      badge={(r) => ({ label: `${r.SESSIONS} ${t('basis.sessions')}`, tone: r.ALLOWED ? 'blue' : r.MULTI_HOST ? 'rose' : 'amber', Icon: UserX })}
                      subtitle={(r) => [r.TERMINALS, r.IPS].filter(Boolean).join(' · ') || '—'}
                      onOpen={(r) => openSessions(r)}
                    />
                  ) : (
                    <Table head={<><Th>User</Th><Th w={70}>{t('basis.sessions2')}</Th><Th>Terminal</Th><Th>IP</Th><Th>{t('basis.firstLogon')}</Th><Th>{t('basis.lastLogon')}</Th><Th>{t('basis.note')}</Th></>}>
                      {ml.rows.map((r, i) => (
                        <tr key={i} onClick={() => openSessions(r)} style={{ cursor: 'pointer' }}
                            onMouseEnter={e => { e.currentTarget.style.background = '#f8fafc'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = ''; }}>
                          <Td mono title={t('basis.clickViewUserSSessions')}>{r.BNAME}</Td>
                          <Td><Badge text={fmt(r.SESSIONS)} color={r.SESSIONS >= 3 ? '#dc2626' : '#d97706'} /></Td>
                          <Td mono title={r.TERMINALS}>{r.TERMINALS || '—'}</Td>
                          <Td mono title={r.IPS}>{r.IPS || '—'}</Td>
                          <Td>{fmtDate(r.FIRST_DATE)} {fmtTime(r.FIRST_TIME)}</Td>
                          <Td>{fmtDate(r.LAST_DATE)} {fmtTime(r.LAST_TIME)}</Td>
                          <Td>
                            <span style={{ display: 'inline-flex', gap: 5, flexWrap: 'wrap' }}>
                              {r.ALLOWED && <Badge text={t('basis.rz11Allowed')} color="#2563eb" />}
                              {wl.ok && !r.ALLOWED && <Badge text={t('basis.notWhitelisted')} color="#dc2626" />}
                              {r.MULTI_HOST && <Badge text={t('basis.diffTerminal')} color="#dc2626" />}
                              {r.IN_MLD && <Badge text="MLD" color="#7c3aed" />}
                            </span>
                          </Td>
                        </tr>
                      ))}
                    </Table>
                  )}
                  {(ml.allowed || []).length > 0 && (
                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 10 }}>
                      {t('basis.rz11Whitelist')} (<code>login/multi_login_users</code>):{' '}
                      <span style={{ fontFamily: 'monospace' }}>{ml.allowed.join(', ')}</span>
                    </div>
                  )}
                  {criteria}
                  {mldOnly}
                </>
              );
            })()}

            {/* User Usage — status data lokal hasil Fetch User Usage (sap_usr_utl_raw) */}
            {shown === 'usage' && (() => {
              const openFetch = () => { setUuMsg(null); setUuModal(true); };
              const fetchBtn = (
                <button onClick={openFetch} disabled={!connId} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: !connId ? '#cbd5e1' : '#2563eb', color: '#fff', border: 'none', borderRadius: 9, padding: '9px 16px', fontSize: 12.5, fontWeight: 600, cursor: !connId ? 'not-allowed' : 'pointer' }}>
                  📡 {t('basis.fetchUserUsage')}
                </button>
              );
              const exportBtn = (
                <button onClick={exportUsage} disabled={!connId || uuExporting}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 7,
                    background: '#f0fdf4', color: '#15803d',
                    border: '1px solid #16794622', borderRadius: 9,
                    padding: '9px 16px', fontSize: 12.5, fontWeight: 600,
                    cursor: (!connId || uuExporting) ? 'not-allowed' : 'pointer',
                    opacity: (!connId || uuExporting) ? 0.6 : 1 }}>
                  <FileSpreadsheet size={14} />
                  {uuExporting ? 'Preparing…' : 'Export Excel'}
                </button>
              );
              if (usageErr) return <SectionError msg={usageErr} />;
              if (!usage) return <Pending text={t('basis.loadingUserUsageStatus')} />;
              if (usage.empty) return (
                <div style={{ padding: '2.25rem', textAlign: 'center', color: '#94a3b8' }}>
                  <BarChart3 size={30} style={{ opacity: 0.5, marginBottom: 10 }} />
                  <div style={{ fontSize: 13.5, marginBottom: 14 }}>
                    {t('basis.noUserUsageDataProfile')}
                  </div>
                  {fetchBtn}
                </div>
              );
              const stale = usageAgeDays != null && usageAgeDays > 30;
              const ageText = usageAgeDays == null ? '' : usageAgeDays === 0
                ? (t('basis.today'))
                : (t('basis.usageagedaysDaySAgo', {
                usageAgeDays
              }));
              const stats = [
                [t('basis.storedSessions'), fmt(usage.sessions)],
                [t('basis.uniqueUsers'), fmt(usage.users)],
                [t('basis.totalHours'), fmt(usage.hours)],
                [t('basis.departments'), fmt(usage.departments)],
                [t('basis.dateCoverage'), usage.firstDate ? `${usage.firstDate} → ${usage.lastDate}` : '—'],
                [t('basis.daysCovered'), fmt(usage.days)],
                [t('basis.fetchBatches'), fmt(usage.batchCount)],
                [t('basis.lastFetch'), usage.lastFetchAt ? new Date(usage.lastFetchAt).toLocaleString() : '—'],
              ];
              return (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10, marginBottom: 14 }}>
                    {stats.map(([k, v]) => (
                      <div key={k} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '9px 13px' }}>
                        <div style={{ fontSize: 10.5, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>{k}</div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginTop: 2 }}>{v || '—'}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', background: stale ? '#fffbeb' : '#f0fdfa', border: `1px solid ${stale ? '#fde68a' : '#99f6e4'}`, borderRadius: 10, padding: '10px 14px', marginBottom: 16 }}>
                    <div style={{ fontSize: 12.5, color: stale ? '#92400e' : '#0f766e', flex: '1 1 240px' }}>
                      {id
                        ? `Diambil ${ageText} oleh ${usage.lastFetchBy || '—'} · rentang terakhir ${usage.lastSourceTag || '—'}.`
                        : `Fetched ${ageText} by ${usage.lastFetchBy || '—'} · last range ${usage.lastSourceTag || '—'}.`}
                      {stale && ' ' + (t('basis.olderThan30DaysConsider'))}
                    </div>
                    {exportBtn}
                    {fetchBtn}
                  </div>
                  {(usage.batches || []).length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 11.5, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 7 }}>{t('basis.fetchBatches')}</div>
                      <DynamicTable rows={usage.batches.map(b => ({
                        [t('basis.range')]: b.source_tag,
                        [t('basis.sessions2')]: fmt(b.sessions),
                        User: fmt(b.users),
                        [t('basis.hours')]: fmt(b.hours),
                        [t('basis.fetched')]: b.fetched_at ? new Date(b.fetched_at).toLocaleString() : '—',
                      }))} />
                    </div>
                  )}
                  {(usage.periods || []).length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 11.5, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 7 }}>{t('basis.period')}</div>
                      <DynamicTable rows={usage.periods.map(p => ({
                        [t('basis.period2')]: p.period || '—',
                        [t('basis.sessions2')]: fmt(p.sessions),
                        User: fmt(p.users),
                        [t('basis.hours')]: fmt(p.hours),
                      }))} />
                    </div>
                  )}
                  {(usage.topUsers || []).length > 0 && (
                    <div>
                      <div style={{ fontSize: 11.5, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 7 }}>{t('basis.topUsersHours')}</div>
                      <DynamicTable rows={usage.topUsers.map(u => ({
                        User: u.user,
                        [t('basis.department')]: u.dept || '—',
                        [t('basis.sessions2')]: fmt(u.sessions),
                        [t('basis.hours')]: fmt(u.hours),
                      }))} />
                    </div>
                  )}
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 12 }}>
                    {t('basis.sourceLocalTableSapUsr')}
                  </div>
                </>
              );
            })()}

            {shown === 'dumps' && (!sd?.ok ? <SectionError msg={sd?.error} /> : (sd.rows || []).length === 0 ? <Empty text={t('basis.noShortDumpsRange')} /> : (
              <>
                <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 10 }}>{t('basis.clickRowViewDumpContent')}</div>
                {isMobile ? (
                  <CardList
                    items={sd.rows.map((r, i) => ({ __i: i, ...r }))}
                    keyOf={(r) => String(r.__i)}
                    title={(r) => r.RUNTIME_ERROR || '—'}
                    badge={(r) => ({ label: r.MANDT ? `cl.${r.MANDT}` : '—', tone: 'rose', Icon: Bug })}
                    subtitle={(r) => [`${fmtDate(r.DATUM)} ${fmtTime(r.UZEIT)}`, r.UNAME, r.PROGRAM].filter(Boolean).join(' · ')}
                    onOpen={(r) => openDump(r)}
                  />
                ) : (
                <Table head={<><Th>{t('basis.date3')}</Th><Th>{t('basis.time')}</Th><Th>User</Th><Th w={64}>Client</Th><Th>Runtime Error</Th><Th>{id ? 'Program' : 'Program'}</Th><Th w={50}></Th></>}>
                  {sd.rows.map((r, i) => (
                    <tr key={i} onClick={() => openDump(r)} style={{ cursor: 'pointer' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                        onMouseLeave={e => e.currentTarget.style.background = ''}>
                      <Td>{fmtDate(r.DATUM)}</Td><Td mono>{fmtTime(r.UZEIT)}</Td><Td mono>{r.UNAME}</Td><Td mono>{r.MANDT}</Td>
                      <Td mono title={r.RUNTIME_ERROR}><span style={{ color: '#dc2626', fontWeight: 600 }}>{r.RUNTIME_ERROR || '—'}</span></Td>
                      <Td mono title={r.PROGRAM}>{r.PROGRAM || '—'}</Td>
                      <Td><Eye size={15} color="#0d9488" /></Td>
                    </tr>
                  ))}
                </Table>
                )}
              </>
            ))}

            {shown === 'locks' && (!le?.ok ? <SectionError msg={le?.error} /> : (le.rows || []).length === 0 ? <Empty text={t('basis.noLockEntriesSm12')} /> : (
              <DynamicTable rows={le.rows} preferred={['GUNAME', 'GNAME', 'GARG', 'GMODE', 'GTHOST', 'GTDATE', 'GTTIME', 'GCLIENT']} />
            ))}

            {shown === 'updates' && (
              ue ? (!ue.ok ? <SectionError msg={ue.error} /> : (ue.rows || []).length === 0 ? <Empty text={t('basis.noUpdateErrors')} /> : <DynamicTable rows={ue.rows} />)
                 : <Pending text={t('basis.updateErrorsSm13BackendNot')} />
            )}

            {shown === 'disk' && (
              dk ? (!dk.ok ? <SectionError msg={dk.error} /> : (dk.rows || []).length === 0 ? <Empty text={t('basis.noDiskData')} /> : <DynamicTable rows={dk.rows} />)
                 : <Pending text={t('basis.diskAseSpaceBackendNot')} />
            )}

            {/* Panel Anomali Login - sumber sap_usr_utl_raw */}
            {/* Panel Capacity - sumber sap_usr_utl_raw */}
                                        {shown === 'dbSpace' && (
                sps ? (!sps.ok ? <SectionError msg={sps.error} /> : (
                  !(sps.segments || []).length
                    ? <Empty text="No space data" />
                    : (
                      <>
                        <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 10 }}>
                          {(sps.command || 'ZASE_SPACE') + ' · ' + sps.segments.length + ' segments · amber = >=85% used, red = >=90%'}
                        </div>
                        <div style={{ overflow: 'auto', border: '1px solid #e2e8f0', borderRadius: 10, maxHeight: '50vh' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                            <thead>
                              <tr>
                                {['Database', 'Segment', 'Total MB', 'Free MB', '% Used'].map((h, i) => (
                                  <th key={h} style={{ padding: '8px 10px', position: 'sticky', top: 0, background: '#0f172a', color: '#94a3b8', textAlign: i < 2 ? 'left' : 'right', fontWeight: 600 }}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {sps.segments.slice().sort((a, b) => (b.usedPct || 0) - (a.usedPct || 0)).map((r) => {
                                const pct = r.usedPct == null ? null : Number(r.usedPct);
                                const crit = pct != null && pct >= 90;
                                const warn = pct != null && pct >= 85;
                                const col = crit ? '#dc2626' : (warn ? '#b45309' : 'inherit');
                                const n = (v) => (v == null ? '-' : Number(v).toLocaleString('en-US'));
                                const td = { padding: '6px 10px', textAlign: 'right' };
                                return (
                                  <tr key={r.db + '|' + r.segment} style={{ borderTop: '1px solid #e2e8f0', color: col }}>
                                    <td style={{ padding: '6px 10px', fontFamily: 'monospace' }}>{r.db}</td>
                                    <td style={{ padding: '6px 10px' }}>{r.segment}</td>
                                    <td style={td}>{n(r.totalMb)}</td>
                                    <td style={td}>{n(r.freeMb)}</td>
                                    <td style={{ ...td, fontWeight: warn || crit ? 700 : 400 }}>{pct == null ? '-' : pct.toFixed(2) + '%'}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                        <div style={{ marginTop: 16 }}>
                          <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 6 }}>Open transactions</div>
                          {!(sps.transactions || []).length
                            ? <div style={{ fontSize: 12.5, color: '#94a3b8' }}>None</div>
                            : (
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                                <tbody>
                                  {sps.transactions.map((t, i) => {
                                    const internal = !t.name || String(t.name).charAt(0) === '$';
                                    const old = Number(t.ageMin || 0) >= 60 && !internal;
                                    return (
                                      <tr key={i} style={{ borderTop: '1px solid #e2e8f0', color: old ? '#dc2626' : (internal ? '#94a3b8' : 'inherit') }}>
                                        <td style={{ padding: '5px 10px', fontFamily: 'monospace' }}>{t.db}</td>
                                        <td style={{ padding: '5px 10px' }}>spid {t.spid}</td>
                                        <td style={{ padding: '5px 10px', fontFamily: 'monospace' }}>{t.name}{internal ? ' (internal)' : ''}</td>
                                        <td style={{ padding: '5px 10px' }}>{t.startTime}</td>
                                        <td style={{ padding: '5px 10px', textAlign: 'right', fontWeight: old ? 700 : 400 }}>{t.ageMin} min</td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            )}
                        </div>
                        <div style={{ marginTop: 16 }}>
                          <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 6 }}>Transaction dumps</div>
                          {!(sps.dumps || []).length
                            ? <div style={{ fontSize: 12.5, color: '#94a3b8' }}>No dump data</div>
                            : (
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                                <tbody>
                                  {sps.dumps.slice().sort((a, b) => (b.ageHrs || 0) - (a.ageHrs || 0)).map((d) => {
                                    const sysDb = ['master', 'model', 'tempdb', 'saptempdb', 'sybsystemdb', 'sybsystemprocs'].indexOf(d.db) >= 0;
                                    const stale = !sysDb && Number(d.ageHrs || 0) >= 24;
                                    const hrs = Number(d.ageHrs || 0);
                                    const age = hrs >= 48 ? Math.floor(hrs / 24) + ' days' : hrs + ' h';
                                    return (
                                      <tr key={d.db} style={{ borderTop: '1px solid #e2e8f0', color: stale ? '#dc2626' : (sysDb ? '#94a3b8' : 'inherit') }}>
                                        <td style={{ padding: '5px 10px', fontFamily: 'monospace' }}>{d.db}{sysDb ? ' (system)' : ''}</td>
                                        <td style={{ padding: '5px 10px' }}>{d.lastTranDump}</td>
                                        <td style={{ padding: '5px 10px', textAlign: 'right', fontWeight: stale ? 700 : 400 }}>{age} ago</td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            )}
                        </div>
                        <div style={{ marginTop: 16 }}>
                          <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 6 }}>Data cache (cumulative since ASE boot)</div>
                          {!(sps.caches || []).length
                            ? <div style={{ fontSize: 12.5, color: '#94a3b8' }}>No cache data</div>
                            : (
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                                <tbody>
                                  {sps.caches.map((c2) => {
                                    const hit = c2.hitPct == null ? null : Number(c2.hitPct);
                                    const idle = !Number(c2.searches || 0);
                                    const low = !idle && hit != null && hit < 90;
                                    return (
                                      <tr key={c2.name} style={{ borderTop: '1px solid #e2e8f0', color: low ? '#dc2626' : (idle ? '#94a3b8' : 'inherit') }}>
                                        <td style={{ padding: '5px 10px', fontFamily: 'monospace' }}>{c2.name}</td>
                                        <td style={{ padding: '5px 10px', textAlign: 'right' }}>{Number(c2.searches || 0).toLocaleString('en-US')} searches</td>
                                        <td style={{ padding: '5px 10px', textAlign: 'right' }}>{Number(c2.physReads || 0).toLocaleString('en-US')} phys reads</td>
                                        <td style={{ padding: '5px 10px', textAlign: 'right', fontWeight: low ? 700 : 400 }}>{idle ? 'unused' : (hit == null ? '-' : hit.toFixed(2) + '%')}</td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            )}
                        </div>
                        <div style={{ marginTop: 16 }}>
                          <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 6 }}>Blocked processes</div>
                          {!(sps.blocking || []).length
                            ? <div style={{ fontSize: 12.5, color: '#94a3b8' }}>None at this snapshot</div>
                            : (
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                                <tbody>
                                  {sps.blocking.map((b) => (
                                    <tr key={b.spid} style={{ borderTop: '1px solid #e2e8f0', color: Number(b.waitSec || 0) >= 60 ? '#dc2626' : 'inherit' }}>
                                      <td style={{ padding: '5px 10px', fontFamily: 'monospace' }}>spid {b.spid}</td>
                                      <td style={{ padding: '5px 10px' }}>blocked by {b.blockedBy}</td>
                                      <td style={{ padding: '5px 10px' }}>{b.login}</td>
                                      <td style={{ padding: '5px 10px', fontFamily: 'monospace' }}>{b.cmd}</td>
                                      <td style={{ padding: '5px 10px' }}>{b.db}</td>
                                      <td style={{ padding: '5px 10px', textAlign: 'right', fontWeight: 700 }}>{b.waitSec} s</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                        </div>
                        <AseTableStats connectionId={connId} />
                        <AseSpaceTrend connectionId={connId} refreshKey={data && data.generatedAt} keys={(sps.segments || []).map(function (r) { return r.db + '|' + r.segment; })} />
                      </>
                    )
                )) : <Pending text="DB space monitoring not configured on backend" />
              )}
              {shown === 'aseConfig' && (
                mc ? (!mc.ok ? <SectionError msg={mc.error} /> : (
                  !(mc.rows || []).length
                    ? <Empty text="No sp_monitorconfig data" />
                    : (
                      <>
                        <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 10 }}>
                          {(mc.command || 'ZASE_MONCONFIG') + ' · ' + mc.rows.length + ' resources · amber = >=80% active, red = >=95% or reused'}
                        </div>
                        <div style={{ overflow: 'auto', border: '1px solid #1e293b', borderRadius: 10, maxHeight: '60vh' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                            <thead>
                              <tr>
                                {['Resource', 'Free', 'Active', '% Active', 'Max Used', 'Reuse cnt'].map((h, i) => (
                                  <th key={h} style={{ padding: '8px 10px', position: 'sticky', top: 0, background: '#0f172a', color: '#94a3b8', textAlign: i ? 'right' : 'left', fontWeight: 600 }}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {mc.rows.map((r) => {
                                const pct = r.pctAct == null ? null : Number(r.pctAct);
                                const reuse = Number(r.reuseCnt || 0);
                                const skip = r.name === 'max memory';
                                const crit = !skip && ((pct != null && pct >= 95) || reuse > 0);
                                const warn = !skip && pct != null && pct >= 80;
                                const col = crit ? '#dc2626' : (warn ? '#b45309' : 'inherit');
                                const n = (v) => (v == null ? '-' : Number(v).toLocaleString('en-US'));
                                const td = { padding: '6px 10px', textAlign: 'right' };
                                return (
                                  <tr key={r.name} style={{ borderTop: '1px solid #1e293b', color: col }}>
                                    <td style={{ padding: '6px 10px', fontFamily: 'monospace' }}>{r.name}</td>
                                    <td style={td}>{n(r.numFree)}</td>
                                    <td style={td}>{n(r.numActive)}</td>
                                    <td style={{ ...td, fontWeight: warn || crit ? 700 : 400 }}>{pct == null ? '-' : pct.toFixed(2) + '%'}</td>
                                    <td style={td}>{n(r.maxUsed)}</td>
                                    <td style={td}>{n(r.reuseCnt)}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                        <AseMonconfigTrend connectionId={connId} refreshKey={data && data.generatedAt} names={mc.rows.map(function (r) { return r.name; })} />
                      </>
                    )
                )) : <Pending text="ASE config monitoring not configured on backend" />
              )}
              {shown === 'sybaseLog' && (
              sl ? (!sl.ok ? <SectionError msg={sl.error} /> : !sl.text ? <Empty text={t('basis.emptyErrorlog')} /> : (
                <>
                  <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 10 }}>{id ? 'ASE errorlog' : 'ASE errorlog'} · {sl.command} · {sl.count} {t('basis.lines')}</div>
                  <div style={{ overflow: 'auto', border: '1px solid #f1f5f9', borderRadius: 10, maxHeight: '60vh', background: '#0f172a' }}>
                    <pre style={{ margin: 0, padding: '14px 16px', fontFamily: 'monospace', fontSize: 11.5, lineHeight: 1.5, color: '#e2e8f0', whiteSpace: 'pre' }}>{sl.text}</pre>
                  </div>
                </>
              )) : <Pending text={t('basis.sybaseLogBackendNotConfigured')} />
            )}
          </div>
        </>
      )}
      {/* Mobile: bottom-sheet detail baris tabel */}
      {isMobile && mrow && (
        <DetailSheet open title={mrow.title} fields={mrow.fields} onClose={() => setMrow(null)} />
      )}
      {/* Modal detail dump */}
      {dump && (
        <div onClick={() => setDump(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, width: 'min(960px, 96vw)', maxHeight: '88vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 50px rgba(0,0,0,0.3)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid #f1f5f9' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                <Bug size={17} color="#dc2626" />
                <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{t('basis.shortDumpDetail')} · {dump.title}</h3>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {!dump.loading && !dump.error && (dump.readable?.length > 0) && (
                  <button onClick={() => setDumpRaw(v => !v)} style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 7, padding: '4px 10px', fontSize: 12, fontWeight: 600, color: '#475569', cursor: 'pointer' }}>
                    {dumpRaw ? (t('basis.summary')) : (t('basis.raw'))}
                  </button>
                )}
                <button onClick={() => setDump(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', display: 'flex' }}><X size={18} /></button>
              </div>
            </div>
            <div style={{ padding: 0, overflow: 'auto', flex: 1 }}>
              {dump.loading ? (
                <div style={{ padding: '2.5rem', textAlign: 'center', color: '#64748b' }}>
                  <RefreshCw size={20} style={{ animation: 'spin 1s linear infinite', marginBottom: 8 }} /><div>{t('basis.fetchingDump')}</div>
                </div>
              ) : dump.error ? (
                <div style={{ padding: 18 }}><SectionError msg={dump.error} /></div>
              ) : !dump.text && !(dump.readable?.length) ? (
                <Empty text={t('basis.emptyDumpContent')} />
              ) : (!dumpRaw && dump.readable?.length) ? (
                <pre style={{ margin: 0, padding: '16px 18px', fontFamily: 'monospace', fontSize: 12, lineHeight: 1.55, color: '#1e293b', whiteSpace: 'pre' }}>{dump.readable.join('\n')}</pre>
              ) : (
                <pre style={{ margin: 0, padding: '16px 18px', fontFamily: 'monospace', fontSize: 11.5, lineHeight: 1.5, color: '#475569', whiteSpace: 'pre' }}>{dump.text}</pre>
              )}
            </div>
            {!dump.loading && dump.segments != null && (
              <div style={{ padding: '8px 18px', borderTop: '1px solid #f1f5f9', fontSize: 11, color: '#94a3b8' }}>{dump.segments} {t('basis.snapSegments')}</div>
            )}
          </div>
        </div>
      )}
      {/* Modal sesi/mode per user (ala SM04) */}
      {sess && (
        <div onClick={() => setSess(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, width: 'min(1000px, 96vw)', maxHeight: '88vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 50px rgba(0,0,0,0.3)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid #f1f5f9' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                <Users size={17} color="#0891b2" />
                <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#0f172a' }}>
                  {t('basis.userSessions')} · {sess.bname}
                </h3>
              </div>
              <button onClick={() => setSess(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', display: 'flex' }}><X size={18} /></button>
            </div>
            <div style={{ overflow: 'auto', flex: 1, padding: 18 }}>
              {sess.loading ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
                  <RefreshCw size={20} style={{ animation: 'spin 1s linear infinite', marginBottom: 8 }} />
                  <div>{t('basis.fetchingSessions')}</div>
                </div>
              ) : sess.error ? <SectionError msg={sess.error} /> : (
                <>
                  {sess.ok && (sess.rows || []).length > 0 ? (
                    <>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginBottom: 11 }}>
                        <Badge text={`${fmt(sess.rows.length)} ${t('basis.sessions')}`} color="#0891b2" />
                        <Badge text={sess.fm} color="#7c3aed" />
                      </div>
                      <DynamicTable rows={sess.rows} preferred={[sess.userField, 'MANDT', 'TERM', 'TERMINAL', 'TCODE', 'REPORT', 'TIME'].filter(Boolean)} />
                    </>
                  ) : (
                    <>
                      <Empty text={sess.ok
                        ? (t('basis.fmReturnedNoSessionsUser'))
                        : (t('basis.noSessionListFmAvailable'))} />
                      {/* Fallback: sesi dari USR41 yang sudah ada di snapshot */}
                      {(() => {
                        const fb = (lo?.rows || []).filter(r => r.BNAME === sess.bname);
                        if (!fb.length) return null;
                        return (
                          <div style={{ marginTop: 14 }}>
                            <div style={{ fontSize: 11.5, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 7 }}>
                              {id ? `Dari ${lo?.table || 'USR41'}` : `From ${lo?.table || 'USR41'}`}
                            </div>
                            <DynamicTable rows={fb.map(r => ({
                              Terminal: r.TERMINAL || '\u2014', IP: r.IP || '\u2014', Client: r.CLIENT,
                              [t('basis.date3')]: fmtDate(r.LOGON_DATE), [t('basis.time')]: fmtTime(r.LOGON_TIME),
                            }))} />
                          </div>
                        );
                      })()}
                    </>
                  )}
                  {(sess.tried || []).length > 0 && (
                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 14, lineHeight: 1.6 }}>
                      {t('basis.fmsTried')}:{' '}
                      {sess.tried.map((t, i) => (
                        <span key={i}>{i > 0 && ' \u00b7 '}<code>{t.fm}</code>/{t.table} {t.ok ? `\u2713 ${fmt(t.rows)}` : `\u2715 ${t.error}`}</span>
                      ))}
                      {sess.totalRows != null && <> \u00b7 {t('basis.totalSystemSessions')}: {fmt(sess.totalRows)}</>}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
